import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { isAxiosError, AxiosError } from 'axios';
import { Button, Card, Row, Col, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FileUp, StickyNote, Edit, Phone, Mail, MapPin, Building2, User, FileText, FileImage, Download, Eye, File, Trash2, Plus, MessageCircle, CreditCard, AlertTriangle, Clock, CheckCircle2, X, Check } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';
import IysIzinModal from '../components/IysIzinModal';
import IysKayitListesi, { IysIzinDetay } from '../components/IysKayitListesi';
import IysKilitliAlan from '../components/IysKilitliAlan';
import SmsGonderModal from '../components/SmsGonderModal';
import SmsGecmisi from '../components/SmsGecmisi';

interface Not {
    notID: number;
    eklenmeTarihi: string;
    aciklama: string;
    ekleyenID: number;
    ekleyen: {
        kullaniciID: number;
        adSoyad: string;
    };
    aktifMi: boolean;
    takipNo: string;
}

interface Dosya {
    dosyaID: number;
    takipNo: string;
    eklenmeTarihi: string;
    aktifMi: boolean;
    dosyaAdi: string;
    aciklama: string;
}

interface Sozlesme {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    sozlesmeTarihi: string;
    rezerveBitisTarihi: string;
    sozlesmeDurumu: string;
    sozlesmeFiyati: number;
    planlananOdeme: number;
    ortalamaVade: number;
    ozelSartlar?: string;
    aktifMi: boolean;
    eklemeTarihi: string;
    cari: {
        cariID: number;
        adiSoyadi: string;
        telefon: string;
        email?: string;
    };
    daire: {
        daireID: number;
        daireNo: string;
        blok?: {
            blokAdi: string;
            proje?: {
                projeID: number;
                projeAdi: string;
            };
        };
    };
}

interface NotForm {
    aciklama: string;
}



const getInitials = (name: string): string =>
    name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

const AVATAR_STYLES = [
    { background: '#e0e7ff', color: '#4338ca' },
    { background: '#ede9fe', color: '#6d28d9' },
    { background: '#dbeafe', color: '#1d4ed8' },
    { background: '#ccfbf1', color: '#0f766e' },
    { background: '#fef3c7', color: '#b45309' },
    { background: '#ffe4e6', color: '#be123c' },
];

const getAvatarStyle = (id: number) => AVATAR_STYLES[id % AVATAR_STYLES.length];

const formatPhoneNumber = (phone: string): string => {
    // Sadece rakamları al
    const numbers = phone.replace(/\D/g, '');
    
    // 10 haneli telefon numarası için format uygula
    if (numbers.length === 10) {
        return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 8)}-${numbers.slice(8)}`;
    }
    
    // Eğer 10 haneli değilse olduğu gibi döndür
    return phone;
};

const unformatPhoneNumber = (phone: string): string => {
    // Sadece rakamları al
    return phone.replace(/\D/g, '');
};

interface Cari {
    cariID: number;
    takipNo: string;
    eklenmeTarihi: string;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    aktifMi: boolean;
    bakiye: number;
}

interface CariForm {
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    aktifMi: boolean;
}

interface CariAltKategori {
    cariAltKategoriID: number;
    turu: string;
    altKategoriAdi: string;
    aktif: boolean;
}

interface KiraSozlesmeOzet {
    kiraSozlesmeID: number;
    takipNo: string;
    baslangicTarihi: string;
    bitisTarihi: string;
    kiraBedeli: number;
    aktifMi: boolean;
    gayrimenkulBaslik: string | null;
}

interface EmlakSunumOzet {
    sunumID: number;
    takipNo: string;
    baslik: string | null;
    durum: string;
    olusturmaTarihi: string;
    aktifMi: boolean;
}

interface Odeme {
    odemeID: number;
    cariID: number;
    islemTarihi: string;
    vadeTarihi: string | null;
    tahsilatTarihi: string | null;
    miktar: number;
    paraBirimiID: number;
    islemTakipNo: string;
    odemeTuru: string;
    odemeYonu: string;
    odemeDurumu: string;
    aciklama: string | null;
    hizmetUrunAdi: string | null;
    aktifMi: boolean;
    sozlesmeOdemesi: boolean;
    kiraSozlesmesiOdemesi: boolean;
    islemTipi?: string;
    projeID: number | null;
    kasaID: number | null;
    sozlesme?: { sozlesmeID: number; sozlesmeTakipNo: string; projeAdi: string | null; daireNo: string | null; } | null;
    kasa?: { kasaID: number; kasaAdi: string; kasaTuru: string; } | null;
}

interface Kasa {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
    aktifMi: boolean;
}

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const formatMoneyInput = (value: string): string => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers === '') return '';
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => value.replace(/\./g, '');

const toTRDate = (iso: string): string => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
};
const fromTRDate = (tr: string): string => {
    if (!tr || tr.length < 10) return '';
    const [d, m, y] = tr.split('.');
    return `${y}-${m}-${d}`;
};
const maskTRDate = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

// UTF-8'in Latin-1 olarak okunmasından kaynaklanan mojibake karakterleri düzeltir
const fixMojibake = (str: string | null | undefined): string | null => {
    if (!str) return str ?? null;
    if (str.split('').some(c => c.charCodeAt(0) > 255)) return str;
    try {
        const bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xFF;
        const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
        if (decoded.length < str.length) return decoded;
        return str;
    } catch { return str; }
};

const MusteriDetay: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const musteriCardRef = useRef<HTMLDivElement>(null);
    const [notlarMaxHeight, setNotlarMaxHeight] = useState<number>(600);
    const [musteri, setMusteri] = useState<Cari | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [iysIzinlerMd, setIysIzinlerMd] = useState<IysIzinDetay[]>([]);
    const [iysModalMd, setIysModalMd] = useState<{iletisimTipi:'TELEFON1'|'TELEFON2'|'EPOSTA';iletisimVerisi:string}|null>(null);
    const [iysAktarimAktif, setIysAktarimAktif] = useState(false);
    const [activeFormTab, setActiveFormTab] = useState<'temel' | 'firma'>('temel');
    const [activeMainTab, setActiveMainTab] = useState<'sozlesmeler' | 'finans' | 'aktiviteler' | 'dosyalar' | 'smsler'>('sozlesmeler');
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [smsRefreshKey, setSmsRefreshKey] = useState(0);
    const [showNotModal, setShowNotModal] = useState(false);
    const [showDosyaModal, setShowDosyaModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [previewType, setPreviewType] = useState<string>('');
    const { user } = useAuth();
    const [notlar, setNotlar] = useState<Not[]>([]);
    const [dosyalar, setDosyalar] = useState<Dosya[]>([]);
    const [sozlesmeler, setSozlesmeler] = useState<Sozlesme[]>([]);
    const [odemeler, setOdemeler] = useState<Odeme[]>([]);
    const [showOdemeEditModal, setShowOdemeEditModal] = useState(false);
    const [selectedOdeme, setSelectedOdeme] = useState<Odeme | null>(null);
    const [kasalar, setKasalar] = useState<Kasa[]>([]);
    const [odemeListeFiltre, setOdemeListeFiltre] = useState<string>('');
    const [showIslemlerMenu, setShowIslemlerMenu] = useState(false);
    const [showHareketModal, setShowHareketModal] = useState(false);
    const [hareketSaving, setHareketSaving] = useState(false);
    const [hareketFormData, setHareketFormData] = useState({
        miktar: '',
        vadeTarihi: '',
        odemeTuru: 'Nakit',
        odemeYonu: 'Tahsilat',
        odemeDurumu: 'Bekliyor',
        aciklama: '',
        kasaID: '',
        sozlesmeSecimi: '',
        kiraSozlesmeSecimi: '',
    });
    const [odemeEditForm, setOdemeEditForm] = useState({
        vadeTarihi: '',
        tahsilatTarihi: '',
        miktar: 0,
        odemeDurumu: '',
        odemeTuru: '',
        aciklama: '',
        kasaID: null as number | null,
    });
    const [saving, setSaving] = useState(false);
    const [activeHareketTab, setActiveHareketTab] = useState<'ekstre' | 'plan'>('ekstre');
    const [hareketFilters, setHareketFilters] = useState({ vadeTarihiBaslangic: '', vadeTarihiBitis: '', odemeTuru: '', odemeDurumu: '' });
    const [selectedTaksitler, setSelectedTaksitler] = useState<Set<number>>(new Set());
    const [taksitTopluSilLoading, setTaksitTopluSilLoading] = useState(false);
    const [odemePlaniSozlesmeFiltre, setOdemePlaniSozlesmeFiltre] = useState<string>('');
    const [showTopluPlanlaModal, setShowTopluPlanlaModal] = useState(false);
    const [topluPlanlaFormData, setTopluPlanlaFormData] = useState({ toplamTutar: '', baslamaTarihi: '', taksitSayisi: '', aralikAy: '1', odemeTuru: 'Nakit', aciklama: '' });
    const [showOdemeYapModal, setShowOdemeYapModal] = useState(false);
    const [odemeYapTaksit, setOdemeYapTaksit] = useState<Odeme | null>(null);
    const [odemeYapFormData, setOdemeYapFormData] = useState({ tarih: '', kasaID: '', odemeTuru: 'Nakit', sozlesmeSecimi: '', kiraSozlesmeSecimi: '', aciklama: '' });
    const [odemeYapTarihDisplay, setOdemeYapTarihDisplay] = useState('');
    const [sozlesmePlanTaksitleri, setSozlesmePlanTaksitleri] = useState<any[]>([]);
    const [isPlanTaksitOdeme, setIsPlanTaksitOdeme] = useState(false);
    const [planTaksitForOdeme, setPlanTaksitForOdeme] = useState<any>(null);
    const [relatedRecords, setRelatedRecords] = useState<{ kiraSozlesmeleri: KiraSozlesmeOzet[]; emlakSunumlari: EmlakSunumOzet[] }>({ kiraSozlesmeleri: [], emlakSunumlari: [] });
    const [notFormData, setNotFormData] = useState<NotForm>({
        aciklama: ''
    });
    const [dosyaFormData, setDosyaFormData] = useState({
        dosya: null as File | null,
        aciklama: ''
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchNotlar = async () => {
        if (musteri?.takipNo) {
            try {
                const response = await axiosInstance.get(`/api/Notlar/takip/${musteri.takipNo}`);
                console.log('Notlar response:', response.data);
                setNotlar(response.data);
            } catch (error) {
                console.error('Notlar error:', error);
                toast.error('Notlar yüklenirken bir hata oluştu');
            }
        }
    };

    const fetchDosyalar = async () => {
        if (musteri?.takipNo && musteri.takipNo !== '') {
            try {
                const response = await axiosInstance.get(`/api/Dosyalar/takip/${musteri.takipNo}`, {
                    params: {
                        dosyaTuru: 1 // Müşteri dosyaları
                    }
                });
                setDosyalar(response.data);
            } catch (error: unknown) {
                console.error('Dosyalar yüklenirken hata:', error);
                // Sadece 404 dışındaki hataları göster
                if (isAxiosError(error) && error.response?.status !== 404) {
                    toast.error('Dosyalar yüklenirken bir hata oluştu');
                }
            }
        } else {
            // Yeni müşteri oluşturma durumunda dosya listesini temizle
            setDosyalar([]);
        }
    };

    const fetchSozlesmeler = async () => {
        if (musteri?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Sozlesmeler?cariID=${musteri.cariID}&aktifMi=true`);
                setSozlesmeler(response.data);
            } catch (error: unknown) {
                console.error('Sözleşmeler yüklenirken hata:', error);
                if (isAxiosError(error) && error.response?.status !== 404) {
                    toast.error('Sözleşmeler yüklenirken bir hata oluştu');
                }
            }
        } else {
            setSozlesmeler([]);
        }
    };

    const fetchOdemeler = async () => {
        if (musteri?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Odemeler?cariID=${musteri.cariID}&aktifMi=true`);
                setOdemeler(response.data);
            } catch (error: unknown) {
                console.error('Ödemeler yüklenirken hata:', error);
            }
        } else {
            setOdemeler([]);
        }
    };

    const fetchKasalar = async () => {
        try {
            const response = await axiosInstance.get('/api/Kasalar?aktifMi=true');
            setKasalar(response.data);
        } catch {
            // sessiz hata
        }
    };

    const getCurrentDate = (): string => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const isTaksitOdeme = (o: Odeme) => /^\d+\.\s*taksit/i.test(o.aciklama || '');
    const isTediyeOdeme = (o: Odeme) => /^Tediye\s*·/i.test(o.aciklama || '');
    const isSozlesmeGeliri = (o: Odeme) => o.islemTakipNo?.startsWith('GEL-SOZ-');
    const isKiraGeliri = (o: Odeme) => o.islemTakipNo?.startsWith('GEL-KRA-');

    const handleTahsilatEkle = () => {
        setHareketFormData({
            miktar: '', vadeTarihi: getCurrentDate(), odemeTuru: 'Nakit',
            odemeYonu: 'Tahsilat', odemeDurumu: 'Bekliyor', aciklama: '',
            kasaID: '', sozlesmeSecimi: '', kiraSozlesmeSecimi: ''
        });
        setShowHareketModal(true);
    };

    const handleOdemeEkle = () => {
        setHareketFormData({
            miktar: '', vadeTarihi: getCurrentDate(), odemeTuru: 'Nakit',
            odemeYonu: 'Odeme', odemeDurumu: 'Bekliyor', aciklama: '',
            kasaID: '', sozlesmeSecimi: '', kiraSozlesmeSecimi: ''
        });
        setShowHareketModal(true);
    };

    const handleSaveHareket = async () => {
        if (!hareketFormData.miktar || !hareketFormData.vadeTarihi || !musteri?.cariID) {
            toast.error('Lütfen zorunlu alanları doldurun!');
            return;
        }
        if (hareketFormData.odemeDurumu === 'Odendi' && !hareketFormData.kasaID) {
            toast.error('Ödenmiş hareketler için kasa seçimi zorunludur!');
            return;
        }
        const toOdemeTuru = (t: string): number => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 }[t] ?? 0);
        const toOdemeYonu = (y: string): number => (y === 'Odeme' ? 1 : 0);
        const toOdemeDurumu = (d: string): number => (d === 'Odendi' ? 1 : 0);

        let islemTakipNo = musteri.takipNo;
        let sozlesmeOdemesi = false;
        let kiraSozlesmesiOdemesi = false;

        if (hareketFormData.sozlesmeSecimi) {
            islemTakipNo = hareketFormData.sozlesmeSecimi;
            sozlesmeOdemesi = true;
        } else if (hareketFormData.kiraSozlesmeSecimi) {
            islemTakipNo = hareketFormData.kiraSozlesmeSecimi;
            kiraSozlesmesiOdemesi = true;
        }

        try {
            setHareketSaving(true);
            await axiosInstance.post('/api/Odemeler', {
                cariID: musteri.cariID,
                islemTarihi: getCurrentDate(),
                vadeTarihi: hareketFormData.vadeTarihi,
                tahsilatTarihi: hareketFormData.vadeTarihi,
                miktar: parseFloat(hareketFormData.miktar),
                paraBirimiID: 1,
                islemTakipNo,
                odemeTuru: toOdemeTuru(hareketFormData.odemeTuru),
                odemeYonu: toOdemeYonu(hareketFormData.odemeYonu),
                islemTipi: toOdemeYonu(hareketFormData.odemeYonu) === 0 ? 2 : 3,
                odemeDurumu: toOdemeDurumu(hareketFormData.odemeDurumu),
                kasaID: hareketFormData.kasaID ? parseInt(hareketFormData.kasaID) : null,
                aciklama: hareketFormData.aciklama || null,
                hizmetUrunAdi: null,
                projeID: null,
                aktifMi: true,
                sozlesmeOdemesi,
                kiraSozlesmesiOdemesi,
                eklemeTarihi: new Date().toISOString()
            });
            toast.success('Kayıt başarıyla eklendi!');
            setShowHareketModal(false);
            fetchOdemeler();
        } catch {
            toast.error('Kayıt eklenirken bir hata oluştu!');
        } finally {
            setHareketSaving(false);
        }
    };

    const handleOdemeEditAc = (odeme: Odeme) => {        setSelectedOdeme(odeme);
        setOdemeEditForm({
            vadeTarihi: odeme.vadeTarihi ? odeme.vadeTarihi.substring(0, 10) : '',
            tahsilatTarihi: odeme.tahsilatTarihi ? odeme.tahsilatTarihi.substring(0, 10) : '',
            miktar: odeme.miktar,
            odemeDurumu: odeme.odemeDurumu,
            odemeTuru: odeme.odemeTuru,
            aciklama: odeme.aciklama || '',
            kasaID: odeme.kasaID,
        });
        setShowOdemeEditModal(true);
    };

    const handleOdemeGuncelle = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedOdeme) return;
        if (odemeEditForm.odemeDurumu === 'Odendi' && !odemeEditForm.kasaID) {
            toast.error('Ödendi durumunda kasa seçimi zorunludur');
            return;
        }
        try {
            await axiosInstance.put(`/api/Odemeler/${selectedOdeme.odemeID}`, {
                odemeID: selectedOdeme.odemeID,
                cariID: selectedOdeme.cariID,
                islemTarihi: selectedOdeme.islemTarihi,
                vadeTarihi: odemeEditForm.vadeTarihi || null,
                tahsilatTarihi: odemeEditForm.tahsilatTarihi || null,
                miktar: odemeEditForm.miktar,
                paraBirimiID: selectedOdeme.paraBirimiID,
                islemTakipNo: selectedOdeme.islemTakipNo,
                odemeTuru: odemeEditForm.odemeTuru,
                odemeYonu: selectedOdeme.odemeYonu,
                odemeDurumu: odemeEditForm.odemeDurumu,
                aciklama: odemeEditForm.aciklama || null,
                hizmetUrunAdi: selectedOdeme.hizmetUrunAdi,
                aktifMi: selectedOdeme.aktifMi,
                sozlesmeOdemesi: selectedOdeme.sozlesmeOdemesi,
                projeID: selectedOdeme.projeID,
                kasaID: odemeEditForm.kasaID,
            });
            toast.success('Ödeme başarıyla güncellendi');
            setShowOdemeEditModal(false);
            fetchOdemeler();
        } catch {
            toast.error('Ödeme güncellenirken bir hata oluştu');
        }
    };

    const handleShowTopluPlanlaModal = () => {
        setTopluPlanlaFormData({ toplamTutar: '', baslamaTarihi: toTRDate(getCurrentDate()), taksitSayisi: '', aralikAy: '1', odemeTuru: 'Nakit', aciklama: '' });
        setShowTopluPlanlaModal(true);
    };

    const handleSaveTopluPlanla = async () => {
        const { toplamTutar, baslamaTarihi: baslamaTarihiTR, taksitSayisi, aralikAy, odemeTuru, aciklama } = topluPlanlaFormData;
        const baslamaTarihi = fromTRDate(baslamaTarihiTR);
        if (!toplamTutar || !baslamaTarihi || !taksitSayisi || !aralikAy) { toast.error('L\u00fctfen zorunlu alanlar\u0131 doldurun!'); return; }
        if (!musteri?.cariID) { toast.error('M\u00fc\u015fteri bilgisi bulunamad\u0131!'); return; }
        const sayi = parseInt(taksitSayisi);
        const tutar = parseFloat(parseMoneyInput(toplamTutar));
        const aralik = parseInt(aralikAy);
        if (sayi <= 0 || tutar <= 0 || aralik <= 0) { toast.error('Ge\u00e7erli de\u011ferler giriniz!'); return; }
        const toOT = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string,number>)[t] ?? 0;
        const taksitTutari = tutar / sayi;
        let kalanTutar = tutar;
        try {
            setSaving(true);
            const promises = [];
            for (let i = 0; i < sayi; i++) {
                const vade = new Date(baslamaTarihi);
                vade.setMonth(vade.getMonth() + i * aralik);
                const miktarBu = i === sayi - 1 ? Math.round(kalanTutar * 100) / 100 : Math.round(taksitTutari * 100) / 100;
                if (i < sayi - 1) kalanTutar -= miktarBu;
                promises.push(axiosInstance.post('/api/Odemeler', {
                    cariID: musteri.cariID, islemTarihi: baslamaTarihi,
                    vadeTarihi: vade.toISOString().split('T')[0], tahsilatTarihi: vade.toISOString().split('T')[0],
                    miktar: miktarBu, paraBirimiID: 1, islemTakipNo: musteri.takipNo,
                    odemeTuru: toOT(odemeTuru), odemeYonu: 0, islemTipi: 2, odemeDurumu: 0,
                    aciklama: `${i + 1}. Taksit${aciklama ? ' - ' + aciklama : ''}`,
                    aktifMi: true, sozlesmeOdemesi: false, eklemeTarihi: new Date().toISOString()
                }));
            }
            await Promise.all(promises);
            toast.success(`${sayi} adet taksit ba\u015far\u0131yla olu\u015fturuldu!`);
            setShowTopluPlanlaModal(false);
            await fetchOdemeler();
        } catch { toast.error('Taksitler olu\u015fturulurken bir hata olu\u015ftu!'); }
        finally { setSaving(false); }
    };

    const fetchSozlesmePlanTaksitleri = async () => {
        if (!musteri?.cariID) return;
        try {
            const res = await axiosInstance.get(`/api/OdemePlanTaksitleri?cariID=${musteri.cariID}`);
            setSozlesmePlanTaksitleri(res.data);
        } catch {
            setSozlesmePlanTaksitleri([]);
        }
    };

    const handlePlanTaksitTahsilatYap = (taksit: any) => {
        setIsPlanTaksitOdeme(true);
        setPlanTaksitForOdeme(taksit);
        setOdemeYapTaksit(null);
        setOdemeYapFormData({ tarih: getCurrentDate(), kasaID: '', odemeTuru: 'Nakit', sozlesmeSecimi: '', kiraSozlesmeSecimi: '', aciklama: taksit.aciklama || '' });
        setOdemeYapTarihDisplay(toTRDate(getCurrentDate()));
        setShowOdemeYapModal(true);
    };

    const handlePlanTaksitGeriAl = async (taksit: any) => {
        if (!window.confirm(`"${taksit.aciklama}" taksitinin tahsilatı geri alınacak. Devam etmek istiyor musunuz?`)) return;
        try {
            setSaving(true);
            if (taksit.gercekOdemeID) {
                await axiosInstance.post(`/api/Odemeler/delete/${taksit.gercekOdemeID}`);
            }
            await axiosInstance.put(`/api/OdemePlanTaksitleri/${taksit.taksitID}/geriali`);
            toast.success('Tahsilat geri alındı.');
            await fetchOdemeler();
            await fetchSozlesmePlanTaksitleri();
        } catch {
            toast.error('Tahsilat geri alma işlemi sırasında bir hata oluştu!');
        } finally {
            setSaving(false);
        }
    };

    const handleDeletePlanTaksit = async (taksit: any) => {
        if (!window.confirm(`"${taksit.aciklama}" planını silmek istediğinize emin misiniz?`)) return;
        try {
            await axiosInstance.delete(`/api/OdemePlanTaksitleri/${taksit.taksitID}`);
            await fetchSozlesmePlanTaksitleri();
            toast.success('Plan satırı silindi.');
        } catch {
            toast.error('Silme işlemi sırasında hata oluştu!');
        }
    };

    const handleOdemeYap = (taksit: Odeme) => {
        setIsPlanTaksitOdeme(false);
        setPlanTaksitForOdeme(null);
        setOdemeYapTaksit(taksit);
        setOdemeYapFormData({ tarih: getCurrentDate(), kasaID: '', odemeTuru: 'Nakit', sozlesmeSecimi: '', kiraSozlesmeSecimi: '', aciklama: taksit.aciklama || '' });
        setOdemeYapTarihDisplay(toTRDate(getCurrentDate()));
        setShowOdemeYapModal(true);
    };

    const handleSaveOdemeYap = async () => {
        // Sözleşme plan taksiti tahsilatı
        if (isPlanTaksitOdeme && planTaksitForOdeme) {
            if (!odemeYapFormData.kasaID) { toast.error('Lütfen kasa seçin!'); return; }
            const toOTPlan = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[t] ?? 0;
            try {
                setSaving(true);
                const odemeRes = await axiosInstance.post('/api/Odemeler', {
                    cariID: musteri!.cariID,
                    islemTarihi: odemeYapFormData.tarih,
                    vadeTarihi: planTaksitForOdeme.vadeTarihi,
                    tahsilatTarihi: odemeYapFormData.tarih,
                    miktar: planTaksitForOdeme.miktar,
                    paraBirimiID: 1,
                    islemTakipNo: planTaksitForOdeme.sozlesmeTakipNo,
                    odemeTuru: toOTPlan(odemeYapFormData.odemeTuru),
                    odemeYonu: 0,
                    odemeDurumu: 1,
                    kasaID: parseInt(odemeYapFormData.kasaID),
                    aciklama: odemeYapFormData.aciklama || planTaksitForOdeme.aciklama,
                    aktifMi: true,
                    sozlesmeOdemesi: true,
                    eklemeTarihi: new Date().toISOString(),
                    islemTipi: 2,
                });
                const yeniOdemeID = odemeRes.data?.odemeID;
                await axiosInstance.put(`/api/OdemePlanTaksitleri/${planTaksitForOdeme.taksitID}/tahsilat`, {
                    gercekOdemeID: yeniOdemeID,
                    tahsilatTarihi: odemeYapFormData.tarih,
                    kasaID: parseInt(odemeYapFormData.kasaID),
                });
                toast.success('Tahsilat başarıyla kaydedildi!');
                setShowOdemeYapModal(false);
                setIsPlanTaksitOdeme(false);
                setPlanTaksitForOdeme(null);
                await fetchOdemeler();
                await fetchSozlesmePlanTaksitleri();
            } catch { toast.error('Tahsilat kaydedilirken bir hata oluştu!'); }
            finally { setSaving(false); }
            return;
        }
        if (!odemeYapTaksit || !musteri?.cariID) return;
        if (!odemeYapFormData.kasaID) { toast.error('L\u00fctfen kasa veya banka se\u00e7in!'); return; }
        const toOT = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[t] ?? 0;
        let islemTakipNo = musteri.takipNo;
        let sozlesmeOdemesi = false;
        let kiraSozlesmesiOdemesi = false;
        if (odemeYapFormData.sozlesmeSecimi) {
            islemTakipNo = odemeYapFormData.sozlesmeSecimi;
            sozlesmeOdemesi = true;
        } else if (odemeYapFormData.kiraSozlesmeSecimi) {
            islemTakipNo = odemeYapFormData.kiraSozlesmeSecimi;
            kiraSozlesmesiOdemesi = true;
        } else if (odemeYapTaksit.kiraSozlesmesiOdemesi) {
            islemTakipNo = odemeYapTaksit.islemTakipNo;
            kiraSozlesmesiOdemesi = true;
        } else if (odemeYapTaksit.sozlesmeOdemesi) {
            islemTakipNo = odemeYapTaksit.islemTakipNo;
            sozlesmeOdemesi = true;
        }
        try {
            setSaving(true);
            // Taksiti Odendi olarak işaretle
            await axiosInstance.put(`/api/Odemeler/${odemeYapTaksit.odemeID}`, {
                odemeID: odemeYapTaksit.odemeID, cariID: odemeYapTaksit.cariID,
                islemTarihi: odemeYapTaksit.islemTarihi, vadeTarihi: odemeYapTaksit.vadeTarihi,
                tahsilatTarihi: odemeYapFormData.tarih, miktar: odemeYapTaksit.miktar,
                paraBirimiID: odemeYapTaksit.paraBirimiID, islemTakipNo: odemeYapTaksit.islemTakipNo,
                odemeTuru: toOT(odemeYapFormData.odemeTuru), odemeYonu: odemeYapTaksit.odemeYonu === 'Odeme' ? 1 : 0, islemTipi: odemeYapTaksit.odemeYonu === 'Odeme' ? 3 : 2, odemeDurumu: 1,
                kasaID: parseInt(odemeYapFormData.kasaID),
                aciklama: odemeYapTaksit.aciklama, hizmetUrunAdi: null,
                aktifMi: true, sozlesmeOdemesi: false, eklemeTarihi: new Date().toISOString(),
                projeID: odemeYapTaksit.projeID,
            });
            toast.success('Tahsilat ba\u015far\u0131yla kaydedildi!');
            setShowOdemeYapModal(false);
            setOdemeYapTaksit(null);
            await fetchOdemeler();
        } catch { toast.error('Tahsilat kaydedilirken bir hata olu\u015ftu!'); }
        finally { setSaving(false); }
    };

    const handleTahsilatGeriAl = async (taksit: Odeme) => {
        if (!window.confirm(`"${taksit.aciklama}" taksitinin tahsilat\u0131 geri al\u0131nacak. Devam etmek istiyor musunuz?`)) return;
        try {
            setSaving(true);
            await axiosInstance.put(`/api/Odemeler/${taksit.odemeID}`, {
                odemeID: taksit.odemeID,
                cariID: taksit.cariID,
                islemTarihi: taksit.islemTarihi,
                vadeTarihi: taksit.vadeTarihi,
                tahsilatTarihi: null,
                miktar: taksit.miktar,
                paraBirimiID: taksit.paraBirimiID,
                islemTakipNo: taksit.islemTakipNo,
                odemeTuru: ({ Nakit: 0, Senet: 1, Cek: 2, Takas: 3 } as Record<string, number>)[taksit.odemeTuru] ?? 0,
                odemeYonu: 0,
                odemeDurumu: 0,
                kasaID: null,
                aciklama: taksit.aciklama,
                hizmetUrunAdi: null,
                aktifMi: true,
                sozlesmeOdemesi: false,
                projeID: taksit.projeID,
            });
            toast.success('Tahsilat geri alındı.');
            await fetchOdemeler();
        } catch {
            toast.error('Tahsilat geri alma i\u015flemi s\u0131ras\u0131nda bir hata olu\u015ftu!');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTaksit = async (odeme: Odeme) => {
        if (!window.confirm(`"${odeme.aciklama}" kayd\u0131n\u0131 silmek istedi\u011finize emin misiniz?`)) return;
        try {
            await axiosInstance.put(`/api/Odemeler/${odeme.odemeID}`, {
                odemeID: odeme.odemeID, cariID: odeme.cariID, islemTarihi: odeme.islemTarihi,
                vadeTarihi: odeme.vadeTarihi, tahsilatTarihi: odeme.tahsilatTarihi, miktar: odeme.miktar,
                paraBirimiID: odeme.paraBirimiID, islemTakipNo: odeme.islemTakipNo,
                odemeTuru: odeme.odemeTuru, odemeYonu: odeme.odemeYonu,
                odemeDurumu: odeme.odemeDurumu, aciklama: odeme.aciklama,
                hizmetUrunAdi: odeme.hizmetUrunAdi, aktifMi: false,
                sozlesmeOdemesi: odeme.sozlesmeOdemesi, eklemeTarihi: new Date().toISOString(),
                projeID: odeme.projeID, kasaID: odeme.kasaID ?? null,
            });
            toast.success('Taksit silindi.');
            await fetchOdemeler();
        } catch { toast.error('Silme i\u015flemi s\u0131ras\u0131nda bir hata olu\u015ftu!'); }
    };

    const handleTopluSilTaksit = async () => {
        if (selectedTaksitler.size === 0) return;
        if (!window.confirm(`${selectedTaksitler.size} taksit kayd\u0131n\u0131 silmek istedi\u011finize emin misiniz?`)) return;
        setTaksitTopluSilLoading(true);
        try {
            await Promise.all(Array.from(selectedTaksitler).map(id => {
                const odeme = odemeler.find(o => o.odemeID === id);
                if (!odeme) return Promise.resolve();
                return axiosInstance.put(`/api/Odemeler/${id}`, {
                    odemeID: odeme.odemeID, cariID: odeme.cariID, islemTarihi: odeme.islemTarihi,
                    vadeTarihi: odeme.vadeTarihi, tahsilatTarihi: odeme.tahsilatTarihi, miktar: odeme.miktar,
                    paraBirimiID: odeme.paraBirimiID, islemTakipNo: odeme.islemTakipNo,
                    odemeTuru: odeme.odemeTuru, odemeYonu: odeme.odemeYonu,
                    odemeDurumu: odeme.odemeDurumu, aciklama: odeme.aciklama,
                    hizmetUrunAdi: odeme.hizmetUrunAdi, aktifMi: false,
                    sozlesmeOdemesi: odeme.sozlesmeOdemesi, eklemeTarihi: new Date().toISOString(),
                    projeID: odeme.projeID, kasaID: odeme.kasaID ?? null,
                });
            }));
            toast.success(`${selectedTaksitler.size} taksit silindi.`);
            setSelectedTaksitler(new Set());
            await fetchOdemeler();
        } catch { toast.error('Toplu silme s\u0131ras\u0131nda bir hata olu\u015ftu!'); }
        finally { setTaksitTopluSilLoading(false); }
    };

    const fetchRelatedRecords = async () => {
        if (musteri?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Cariler/${musteri.cariID}/related`);
                setRelatedRecords(response.data);
            } catch {
                // sessiz hata
            }
        }
    };

    const isPreviewable = (fileName: string): boolean => {
        const ext = fileName.toLowerCase().split('.').pop();
        return ['pdf', 'jpg', 'jpeg', 'png'].includes(ext || '');
    };

    const handlePreview = async (dosyaID: number, dosyaAdi: string) => {
        try {
            const ext = dosyaAdi.toLowerCase().split('.').pop() || '';
            const isPdf = ext === 'pdf';
            const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);

            if (!isPdf && !isImage) {
                toast.info('Bu dosya formatı önizlenemiyor. İndirme başlatılıyor...');
                handleDosyaDownload(dosyaID);
                return;
            }

            const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
                responseType: 'blob',
                headers: {
                    'Accept': isPdf ? 'application/pdf' : 'image/*'
                }
            });
            
            const contentType = response.headers['content-type'];
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            
            setPreviewUrl(url);
            setPreviewType(contentType);
            setShowPreviewModal(true);
        } catch (error) {
            console.error('Dosya önizleme hatası:', error);
            toast.error('Dosya görüntülenirken bir hata oluştu');
        }
    };

    const handleDosyaDownload = async (dosyaID: number) => {
        try {
            const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/octet-stream'
                }
            });
            
            // Content-Disposition header'ından dosya adını al
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'indirilen-dosya';
            
            if (contentDisposition) {
                // RFC 5987 encoding için kontrol
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                    // URL-encoded dosya adını decode et
                    try {
                        fileName = decodeURIComponent(fileName);
                    } catch (e) {
                        console.warn('Dosya adı decode edilemedi:', e);
                    }
                }
            }

            // Dosyayı indir
            const url = window.URL.createObjectURL(new Blob([response.data], { 
                type: response.headers['content-type'] 
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Dosya indirme hatası:', error);
            toast.error('Dosya indirilirken bir hata oluştu');
        }
    };
    const [formData, setFormData] = useState<CariForm>({
        adiSoyadi: '',
        firmaAdi: null,
        telefon1: '',
        telefon2: null,
        ePosta: null,
        adres: null,
        tc: null,
        cariVD: null,
        cariVN: null,
        turu: 'Müşteri',
        turu2: null,
        aktifMi: true
    });

    const [cariAltKategoriler, setCariAltKategoriler] = useState<CariAltKategori[]>([]);

    const fetchCariAltKategoriler = async () => {
        try {
            const response = await axiosInstance.get('/api/CariAltKategorileri/aktif?turu=Müşteri');
            setCariAltKategoriler(response.data);
        } catch {
            // sessiz hata
        }
    };

    const fetchMusteri = async () => {
        if (id === 'yeni') {
            setMusteri({
                cariID: 0,
                takipNo: '',
                eklenmeTarihi: new Date().toISOString(),
                adiSoyadi: '',
                firmaAdi: null,
                telefon1: '',
                telefon2: null,
                ePosta: null,
                adres: null,
                tc: null,
                cariVD: null,
                cariVN: null,
                turu: 'Müşteri',
                turu2: null,
                aktifMi: true,
                bakiye: 0
            });
            setFormData({
                adiSoyadi: '',
                firmaAdi: null,
                telefon1: '',
                telefon2: null,
                ePosta: null,
                adres: null,
                tc: null,
                cariVD: null,
                cariVN: null,
                turu: 'Müşteri',
                turu2: null,
                aktifMi: true
            });
            setActiveFormTab('temel');
            setShowModal(true);
        } else {
            try {
                const response = await axiosInstance.get(`/api/Cariler/${id}`);
                const fixedAdres = fixMojibake(response.data.adres);
                if (response.data.turu !== 'Müşteri') {
                    navigate(`/cariler/${id}`, { replace: true });
                    return;
                }
                setMusteri({ ...response.data, adres: fixedAdres });
                fetchNotlar();
                setFormData({
                    adiSoyadi: response.data.adiSoyadi,
                    firmaAdi: response.data.firmaAdi,
                    telefon1: response.data.telefon1,
                    telefon2: response.data.telefon2,
                    ePosta: response.data.ePosta,
                    adres: fixedAdres,
                    tc: response.data.tc,
                    cariVD: response.data.cariVD,
                    cariVN: response.data.cariVN,
                    turu: response.data.turu,
                    turu2: response.data.turu2,
                    aktifMi: response.data.aktifMi
                });
            } catch (error) {
                toast.error('Müşteri bilgileri yüklenirken bir hata oluştu');
                navigate('/musteriler');
            }
        }
    };

    useEffect(() => {
        fetchMusteri();
        fetchCariAltKategoriler();
        axiosInstance.get('/api/IysAktarimAyarlari/aktif').then(r => setIysAktarimAktif(r.data.aktif)).catch(() => {});
    }, [id]);

    useEffect(() => {
        if (musteri?.takipNo) {
            fetchNotlar();
            fetchDosyalar();
        }
        if (musteri?.cariID) {
            fetchSozlesmeler();
            fetchOdemeler();
            fetchSozlesmePlanTaksitleri();
            fetchRelatedRecords();
            fetchKasalar();
        }
    }, [musteri?.takipNo, musteri?.cariID]);

    // Müşteri kartının yüksekliğini izle
    useEffect(() => {
        const updateNotlarHeight = () => {
            if (musteriCardRef.current) {
                const cardHeight = musteriCardRef.current.offsetHeight;
                // Card.Header yüksekliğini düş (yaklaşık 50px)
                setNotlarMaxHeight(cardHeight - 50);
            }
        };

        // İlk yükleme ve pencere boyutu değiştiğinde güncelle
        updateNotlarHeight();
        window.addEventListener('resize', updateNotlarHeight);

        // Cleanup
        return () => {
            window.removeEventListener('resize', updateNotlarHeight);
        };
    }, [musteri]); // musteri değiştiğinde de kontrol et

    const handleNotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            console.log('Submitting note with data:', {
                aciklama: notFormData.aciklama,
                ekleyenID: user?.kullaniciID,
                aktifMi: true,
                takipNo: musteri?.takipNo
            });
                await axiosInstance.post('/api/Notlar', {
                aciklama: notFormData.aciklama,
                ekleyenID: user?.kullaniciID,
                aktifMi: true,
                takipNo: musteri?.takipNo
            });
            toast.success('Not başarıyla eklendi');
            setShowNotModal(false);
            setNotFormData({ aciklama: '' });
            fetchNotlar();
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Not eklenirken bir hata oluştu');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (id === 'yeni') {
                const response = await axiosInstance.post('/api/Cariler', formData);
                toast.success('Müşteri başarıyla eklendi');
                setShowModal(false);
                navigate(`/musteriler/${response.data.cariID}`);
            } else {
                await axiosInstance.put(`/api/Cariler/${id}`, formData);
                toast.success('Müşteri bilgileri güncellendi');
                fetchMusteri();
                setShowModal(false);
            }
        } catch (error: any) {
            const msg = error?.response?.data?.message;
            toast.error(msg || (id === 'yeni' ? 'Müşteri eklenirken bir hata oluştu' : 'Güncelleme sırasında bir hata oluştu'));
        }
    };

    if (!musteri) {
        return <div>Yükleniyor...</div>;
    }

    return (
        <div>
            <section className="content" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Geri Dön */}
                    <div style={{ marginBottom: 16 }}>
                        <button
                            onClick={() => navigate(-1)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s', letterSpacing: '0.01em' }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                        >
                            <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> Geri Dön
                        </button>
                    </div>
                    {/* Hero Card - Birleşik Kart */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '1.25rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                                overflow: 'hidden'
                            }}>
                                {/* Üst Kısım - Avatar ve Bilgiler */}
                                <div style={{
                                    padding: '2rem 2rem 1.5rem',
                                    borderBottom: '1px solid #f1f5f9'
                                }}>
                                    <div className="d-flex align-items-start justify-content-between gap-4 flex-wrap">
                                        <div className="d-flex align-items-start gap-4 flex-1">
                                            {/* İsim ve Durum */}
                                            <div style={{ flex: 1 }}>
                                                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                                    <h1 style={{
                                                        fontSize: '1.35rem',
                                                        fontWeight: '700',
                                                        color: '#0f172a',
                                                        margin: 0,
                                                        lineHeight: 1.2,
                                                        letterSpacing: '-0.02em'
                                                    }}>
                                                        {musteri?.adiSoyadi || 'Yükleniyor...'}
                                                    </h1>
                                                    
                                                    <span
                                                        style={{
                                                            padding: '0.375rem 0.875rem',
                                                            borderRadius: '9999px',
                                                            fontSize: '0.8125rem',
                                                            fontWeight: '700',
                                                            background: musteri?.aktifMi ? '#dcfce7' : '#fef2f2',
                                                            color: musteri?.aktifMi ? '#15803d' : '#dc2626',
                                                            border: '1px solid',
                                                            borderColor: musteri?.aktifMi ? '#a7f3d0' : '#fecaca',
                                                            lineHeight: 1,
                                                            letterSpacing: '0.02em'
                                                        }}
                                                    >
                                                        {musteri?.aktifMi ? 'Aktif' : 'Pasif'}
                                                    </span>
                                                </div>
                                                
                                                {musteri?.firmaAdi && (
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        color: '#64748b',
                                                        fontWeight: '500',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {musteri.firmaAdi}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                                            <button
                                                onClick={() => setShowDosyaModal(true)}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Dosya Ekle"
                                            >
                                                <FileUp size={18} />
                                            </button>
                                            <button
                                                onClick={() => setShowNotModal(true)}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Not Ekle"
                                            >
                                                <StickyNote size={18} />
                                            </button>
                                            <button
                                                onClick={() => navigate('/satis-sozlesmesi', { state: { selectedCustomer: musteri } })}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.375rem',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Yeni Sözleşme"
                                            >
                                                <Plus size={18} />
                                            </button>
                                            <button
                                                onClick={() => { setActiveFormTab('temel'); setShowModal(true); if(musteri?.cariID){ axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri.cariID}`).then(r=>setIysIzinlerMd(r.data)).catch(()=>{}); } }}
                                                style={{
                                                    padding: '0.625rem 1.125rem',
                                                    background: '#cd6906',
                                                    border: 'none',
                                                    borderRadius: '0.5rem',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontWeight: '600',
                                                    fontSize: '0.875rem',
                                                    boxShadow: '0 2px 4px rgba(205, 105, 6, 0.2)'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#b85d05';
                                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(205, 105, 6, 0.3)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = '#cd6906';
                                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(205, 105, 6, 0.2)';
                                                }}
                                                title="Düzenle"
                                            >
                                                <Edit size={16} />
                                                Düzenle
                                            </button>
                                            {/* İşlemler Dropdown */}
                                            {musteri.cariID !== 0 && (
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    onClick={() => setShowIslemlerMenu(o => !o)}
                                                    style={{
                                                        padding: '0.625rem 1.125rem',
                                                        background: showIslemlerMenu ? '#1a2340' : '#0f172a',
                                                        border: 'none',
                                                        borderRadius: '0.5rem',
                                                        color: 'white',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.375rem',
                                                        fontWeight: '600',
                                                        fontSize: '0.875rem',
                                                    }}
                                                    onMouseOver={(e) => { e.currentTarget.style.background = '#1e293b'; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.background = showIslemlerMenu ? '#1a2340' : '#0f172a'; }}
                                                >
                                                    <i className="fas fa-bolt" style={{ fontSize: '0.75rem' }}></i>
                                                    İşlemler
                                                    <i className={`fas fa-chevron-${showIslemlerMenu ? 'up' : 'down'}`} style={{ fontSize: '0.65rem' }}></i>
                                                </button>
                                                {showIslemlerMenu && (
                                                    <>
                                                        <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowIslemlerMenu(false)} />
                                                        <div style={{
                                                            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                                            background: 'white', border: '1px solid #e2e8f0',
                                                            borderRadius: '0.625rem', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                                            minWidth: 185, zIndex: 1000, overflow: 'hidden'
                                                        }}>
                                                            {([
                                                                { label: 'Tahsilat Ekle', icon: 'fa-plus', color: '#cd6906', onClick: handleTahsilatEkle },
                                                                { label: 'Ödeme Ekle', icon: 'fa-minus', color: '#dc2626', onClick: handleOdemeEkle },
                                                            ] as { label: string; icon: string; color: string; onClick: () => void }[]).map((item, idx, arr) => (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => { item.onClick(); setShowIslemlerMenu(false); }}
                                                                    style={{
                                                                        width: '100%', padding: '0.65rem 1rem',
                                                                        background: 'transparent', border: 'none',
                                                                        borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                                        display: 'flex', alignItems: 'center', gap: '0.625rem',
                                                                        cursor: 'pointer', color: '#374151', fontWeight: '500',
                                                                        fontSize: '0.875rem', textAlign: 'left' as const
                                                                    }}
                                                                    onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; }}
                                                                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
                                                                >
                                                                    <i className={`fas ${item.icon}`} style={{ color: item.color, fontSize: '0.8rem', width: 16 }}></i>
                                                                    {item.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Alt Kısım - İletişim Bilgileri */}
                                <div style={{ padding: '1.75rem 2rem' }}>
                                    <h3 style={{
                                        fontSize: '0.8125rem',
                                        fontWeight: '700',
                                        color: '#94a3b8',
                                        letterSpacing: '0.1em',
                                        marginBottom: '1.25rem',
                                        textTransform: 'uppercase'
                                        }}>İletişim Bilgileri</h3>
                                    <Row>
                                        {/* Telefon 1 - 1/4 */}
                                        <Col md={3}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Phone size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>TELEFON 1</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', marginBottom: '0.5rem' }}>
                                                        {formatPhoneNumber(musteri.telefon1)}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                                                        <a href={`tel:+90${musteri.telefon1}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                            <Phone size={12} /> Ara
                                                        </a>
                                                        <a href={`https://wa.me/90${musteri.telefon1}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#25d366', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                            <i className="fab fa-whatsapp" style={{ fontSize: 12 }}></i> WhatsApp
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowSmsModal(true)}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#eef2ff', color: '#6366f1', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #c7d2fe', cursor: 'pointer' }}
                                                        >
                                                            <MessageCircle size={12} /> SMS Gönder
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Telefon 2 */}
                                            {musteri.telefon2 && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1.25rem' }}>
                                                    <div style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                        borderRadius: '0.5rem',
                                                        background: '#fff8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <Phone size={16} style={{ color: '#cd6906' }} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>TELEFON 2</div>
                                                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', marginBottom: '0.5rem' }}>
                                                            {formatPhoneNumber(musteri.telefon2)}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                                                            <a href={`tel:+90${musteri.telefon2}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                                <Phone size={12} /> Ara
                                                            </a>
                                                            <a href={`https://wa.me/90${musteri.telefon2}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#25d366', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                                <i className="fab fa-whatsapp" style={{ fontSize: 12 }}></i> WhatsApp
                                                            </a>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowSmsModal(true)}
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#eef2ff', color: '#6366f1', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #c7d2fe', cursor: 'pointer' }}
                                                            >
                                                                <MessageCircle size={12} /> SMS Gönder
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        
                                        {/* E-Posta - 1/4 */}
                                        <Col md={3}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Mail size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>E-POSTA</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', wordBreak: 'break-word' }}>
                                                        {musteri.ePosta || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        
                                        {/* Adres - 1/2 */}
                                        <Col md={6}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <MapPin size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>ADRES</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', lineHeight: '1.6', fontWeight: '500' }}>
                                                        {musteri.adres || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Firma Adı */}
                                            {musteri.firmaAdi && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1.25rem' }}>
                                                    <div style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                        borderRadius: '0.5rem',
                                                        background: '#fff8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <Building2 size={16} style={{ color: '#cd6906' }} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>FİRMA ADI</div>
                                                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600' }}>
                                                            {musteri.firmaAdi}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Ana Sekme Navigasyonu */}
                    <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', gap: 0 }}>
                            {([
                                { key: 'sozlesmeler' as const, label: 'Sözleşmeler', icon: 'fa-file-alt' },
                                { key: 'finans' as const, label: 'Finans & Cari', icon: 'fa-chart-bar' },
                                { key: 'aktiviteler' as const, label: 'Aktiviteler', icon: 'fa-clock' },
                                { key: 'dosyalar' as const, label: 'Dosyalar', icon: 'fa-paperclip' },
                                { key: 'smsler' as const, label: 'SMS Geçmişi', icon: 'fa-comment-sms' },
                            ]).map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveMainTab(tab.key)}
                                    style={{
                                        padding: '0.75rem 1.25rem',
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: activeMainTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
                                        color: activeMainTab === tab.key ? '#2563eb' : '#6b7280',
                                        fontWeight: activeMainTab === tab.key ? '600' : '500',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        marginBottom: '-1px',
                                        whiteSpace: 'nowrap' as const,
                                    }}
                                    onMouseOver={e => { if (activeMainTab !== tab.key) e.currentTarget.style.color = '#374151'; }}
                                    onMouseOut={e => { if (activeMainTab !== tab.key) e.currentTarget.style.color = '#6b7280'; }}
                                >
                                    <i className={`fas ${tab.icon}`} style={{ fontSize: '0.75rem' }}></i>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* SOZLESMELER TAB */}
                    {activeMainTab === 'sozlesmeler' && (<>
                    {/* Sözleşmeler - Tam Genişlik */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '1rem',
                                overflow: 'hidden',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                            }}>
                                <div style={{
                                    padding: '1.25rem 1.5rem',
                                    borderBottom: '1px solid #f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <h3 style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: '#0f172a',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FileText size={20} style={{ color: '#cd6906' }} />
                                        İlişki Geçmişi &amp; Sözleşmeler
                                        <span style={{
                                            background: '#f1f5f9',
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            padding: '2px 10px',
                                            borderRadius: '10px',
                                            marginLeft: '0.25rem'
                                        }}>{sozlesmeler.length}</span>
                                    </h3>
                                    {sozlesmeler.length > 0 && (
                                        <div style={{ fontSize: '0.875rem', color: '#475569', fontWeight: 600 }}>
                                            Toplam: ₺{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(sozlesmeler.reduce((sum, s) => sum + s.sozlesmeFiyati, 0))}
                                        </div>
                                    )}
                                </div>

                                {/* Tablo Başlık */}
                                {sozlesmeler.length > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '10px 20px 10px 24px',
                                        borderBottom: '1px solid #f1f5f9',
                                        fontSize: '0.6875rem',
                                        fontWeight: '600',
                                        color: '#94a3b8',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase' as const,
                                        gap: 0
                                    }}>
                                        <div style={{ flex: '0 0 220px' }}>Proje / Blok</div>
                                        <div style={{ flex: '0 0 100px' }}>Daire</div>
                                        <div style={{ flex: '0 0 130px' }}>Durum</div>
                                        <div style={{ flex: '0 0 130px' }}>Sözleşme Tarihi</div>
                                        <div style={{ flex: 1 }}>Fiyat</div>
                                        <div style={{ flex: '0 0 100px', textAlign: 'center' }}>İşlemler</div>
                                    </div>
                                )}

                                <div>
                                    {sozlesmeler.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '4rem 2rem', 
                                            color: '#94a3b8',
                                            fontSize: '0.9375rem'
                                        }}>
                                            <FileText size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                                            <div style={{ fontWeight: '500' }}>
                                                Bu müşteriye ait aktif sözleşme bulunmamaktadır.
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '0.5rem' }}>
                                            {sozlesmeler.map((sozlesme) => {
                                                const durumConfig: Record<string, { border: string; bg: string; badgeBg: string; badgeColor: string }> = {
                                                    'Sözleşme':   { border: '#3b82f6', bg: '#eff6ff', badgeBg: '#dbeafe', badgeColor: '#1d4ed8' },
                                                    'Rezerve':    { border: '#f59e0b', bg: '#fffbeb', badgeBg: '#fef9c3', badgeColor: '#ca8a04' },
                                                    'ArsaSahibi': { border: '#8b5cf6', bg: '#f5f3ff', badgeBg: '#ede9fe', badgeColor: '#6d28d9' },
                                                    'Taşeron':    { border: '#0ea5e9', bg: '#f0f9ff', badgeBg: '#e0f2fe', badgeColor: '#0369a1' },
                                                    'Aktif':      { border: '#10b981', bg: '#ecfdf5', badgeBg: '#dcfce7', badgeColor: '#15803d' },
                                                };
                                                const cfg = durumConfig[sozlesme.sozlesmeDurumu] || { border: '#94a3b8', bg: '#f8fafc', badgeBg: '#f1f5f9', badgeColor: '#475569' };

                                                return (
                                                    <div 
                                                        key={sozlesme.sozlesmeID} 
                                                        style={{
                                                            display: 'flex',
                                                            borderRadius: '14px',
                                                            border: '1.5px solid #f1f5f9',
                                                            background: '#fff',
                                                            marginBottom: '0.5rem',
                                                            overflow: 'hidden',
                                                            transition: 'all 0.18s',
                                                            cursor: 'pointer'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.currentTarget.style.background = '#fef9f4';
                                                            e.currentTarget.style.boxShadow = '0 4px 18px rgba(15,23,42,0.10)';
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                            e.currentTarget.style.transform = 'none';
                                                        }}
                                                        onClick={() => navigate(`/sozlesme-detay/${sozlesme.sozlesmeTakipNo}`)}
                                                    >
                                                        {/* Sol Renkli Accent */}
                                                        <div style={{
                                                            width: '4px',
                                                            borderRadius: '14px 0 0 14px',
                                                            flexShrink: 0,
                                                            background: cfg.border
                                                        }} />

                                                        {/* İçerik */}
                                                        <div style={{
                                                            padding: '16px 20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flex: 1,
                                                            gap: 0,
                                                            minWidth: 0
                                                        }}>
                                                            {/* Proje / Blok */}
                                                            <div style={{ flex: '0 0 220px', overflow: 'hidden' }}>
                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    fontWeight: 600,
                                                                    color: '#0f172a',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {sozlesme.daire?.blok?.proje?.projeAdi || '—'}
                                                                </div>
                                                                {sozlesme.daire?.blok?.blokAdi && (
                                                                    <div style={{
                                                                        fontSize: '12px',
                                                                        color: '#64748b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        marginTop: '2px'
                                                                    }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#94a3b8" width="11" height="11">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                                                                        </svg>
                                                                        {sozlesme.daire.blok.blokAdi}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Daire */}
                                                            <div style={{ flex: '0 0 100px' }}>
                                                                <span style={{
                                                                    background: '#f1f5f9',
                                                                    color: '#475569',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    padding: '5px 12px',
                                                                    borderRadius: '8px',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {sozlesme.daire?.daireNo ? `Daire ${sozlesme.daire.daireNo}` : '—'}
                                                                </span>
                                                            </div>

                                                            {/* Durum */}
                                                            <div style={{ flex: '0 0 130px' }}>
                                                                <span style={{
                                                                    background: cfg.badgeBg,
                                                                    color: cfg.badgeColor,
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    padding: '4px 12px',
                                                                    borderRadius: '20px',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px'
                                                                }}>
                                                                    <span style={{
                                                                        width: '6px',
                                                                        height: '6px',
                                                                        borderRadius: '50%',
                                                                        background: cfg.border,
                                                                        flexShrink: 0
                                                                    }} />
                                                                    {sozlesme.sozlesmeDurumu === 'ArsaSahibi' ? 'Arsa Sahibi' : sozlesme.sozlesmeDurumu}
                                                                </span>
                                                            </div>

                                                            {/* Tarih */}
                                                            <div style={{ flex: '0 0 130px' }}>
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#94a3b8',
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase' as const,
                                                                    letterSpacing: '0.3px'
                                                                }}>TARİH</div>
                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    color: '#334155',
                                                                    fontWeight: 600
                                                                }}>
                                                                    {new Date(sozlesme.sozlesmeTarihi).toLocaleDateString('tr-TR')}
                                                                </div>
                                                            </div>

                                                            {/* Fiyat */}
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#94a3b8',
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase' as const,
                                                                    letterSpacing: '0.3px'
                                                                }}>SÖZLEŞME FİYATI</div>
                                                                <div style={{
                                                                    fontSize: '15px',
                                                                    color: '#0f172a',
                                                                    fontWeight: 600
                                                                }}>
                                                                    ₺{new Intl.NumberFormat('tr-TR', {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2
                                                                    }).format(sozlesme.sozlesmeFiyati)}
                                                                </div>
                                                            </div>

                                                            {/* İşlemler */}
                                                            <div style={{ flex: '0 0 100px', textAlign: 'center' }}>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/sozlesme-detay/${sozlesme.sozlesmeTakipNo}`);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        background: 'transparent',
                                                                        border: '1px solid #cd6906',
                                                                        borderRadius: '6px',
                                                                        color: '#cd6906',
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.15s',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '6px'
                                                                    }}
                                                                    onMouseOver={(e) => {
                                                                        e.currentTarget.style.background = '#cd6906';
                                                                        e.currentTarget.style.color = '#fff';
                                                                    }}
                                                                    onMouseOut={(e) => {
                                                                        e.currentTarget.style.background = 'transparent';
                                                                        e.currentTarget.style.color = '#cd6906';
                                                                    }}
                                                                >
                                                                    <Eye size={12} />
                                                                    Detaylar
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    </>)}

                    {/* FİNANS & CARİ TAB */}
                    {activeMainTab === 'finans' && (() => {
                        // Sadece kesinleşmiş sözleşmeler cari hesaba dahil edilir (Rezerve ve Satışta hariç)
                        const cariSozlesmeler = sozlesmeler.filter(sz => sz.sozlesmeDurumu === 'Sözleşme' || sz.sozlesmeDurumu === 'Taşeron' || sz.sozlesmeDurumu === 'ArsaSahibi');
                        // Borç/Alacak hesapları
                        // sozlesmeOdemesi=true ise tur değil odemeYonu'na göre belirlenir
                        const sozlesmeBorc = cariSozlesmeler.reduce((s, sz) => s + (sz.sozlesmeFiyati || 0), 0);
                        const genelToplamBorc = sozlesmeBorc +
                            odemeler.filter(o => !isSozlesmeGeliri(o) && (
                                (!o.sozlesmeOdemesi && o.islemTipi === 'Gelir') ||
                                (o.sozlesmeOdemesi && o.odemeYonu === 'Odeme' && o.odemeDurumu === 'Odendi') ||
                                (!o.sozlesmeOdemesi && o.islemTipi === 'Odeme' && o.odemeDurumu === 'Odendi')
                            )).reduce((s, o) => s + o.miktar, 0);
                        const genelToplamAlacak =
                            odemeler.filter(o => !isSozlesmeGeliri(o) && (
                                o.islemTipi === 'Gider' ||
                                (!o.sozlesmeOdemesi && o.islemTipi === 'Tahsilat' && o.odemeDurumu === 'Odendi') ||
                                (o.sozlesmeOdemesi && o.odemeYonu === 'Tahsilat' && o.odemeDurumu === 'Odendi')
                            )).reduce((s, o) => s + o.miktar, 0);
                        const netBakiye = genelToplamBorc - genelToplamAlacak;

                        // Ekstre satırları
                        const expandedRows: Array<{
                            key: string; type: 'sozlesme' | 'fatura' | 'makbuz';
                            date: string; label: string; subLabel?: string;
                            rowBorc: number | null; rowAlacak: number | null;
                            durum?: string; kasaAdi?: string; orijinalOdeme?: Odeme;
                        }> = [];

                        cariSozlesmeler.forEach(sz => {
                            const projeAdi = sz.daire?.blok?.proje?.projeAdi || '';
                            const daireNo = sz.daire?.daireNo || '';
                            expandedRows.push({
                                key: `sozlesme-${sz.sozlesmeID}`, type: 'sozlesme',
                                date: sz.sozlesmeTarihi, label: 'Sat\u0131\u015f S\u00f6zle\u015fmesi',
                                subLabel: `${projeAdi}${daireNo ? ' - Daire ' + daireNo : ''}`,
                                rowBorc: sz.sozlesmeFiyati, rowAlacak: null, durum: sz.sozlesmeDurumu,
                            });
                        });

                        odemeler.filter(o => !isSozlesmeGeliri(o)).forEach(o => {
                            const tur = o.islemTipi || '';
                            // Bekliyor olan kayıtlar ekstre'de gizlenir
                            if (o.odemeDurumu === 'Bekliyor') return;

                            // Sözleşme ödemeleri: odemeYonu'na göre Borç/Alacak
                            if (o.sozlesmeOdemesi) {
                                if (o.odemeYonu === 'Tahsilat' && o.odemeDurumu === 'Odendi') {
                                    // Sözleşme tahsilatı → Alacak
                                    expandedRows.push({
                                        key: `soz-tahsilat-${o.odemeID}`, type: 'makbuz',
                                        date: o.tahsilatTarihi || o.islemTarihi, label: 'Tahsilat',
                                        subLabel: o.aciklama || '',
                                        rowBorc: null, rowAlacak: o.miktar,
                                        durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                    });
                                } else if (o.odemeYonu === 'Odeme' && o.odemeDurumu === 'Odendi') {
                                    // Sözleşme ödemesi → Borç
                                    expandedRows.push({
                                        key: `soz-odeme-${o.odemeID}`, type: 'fatura',
                                        date: o.tahsilatTarihi || o.islemTarihi, label: 'Ödeme',
                                        subLabel: o.aciklama || '',
                                        rowBorc: o.miktar, rowAlacak: null,
                                        durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                    });
                                }
                                return;
                            }

                            // Normal ödemeler: tur'a göre
                            if (tur === 'Gider') {
                                // Gider → Alacak
                                expandedRows.push({
                                    key: `gider-${o.odemeID}`, type: 'fatura',
                                    date: o.islemTarihi, label: o.aciklama || o.odemeTuru,
                                    rowBorc: null, rowAlacak: o.miktar,
                                    durum: o.odemeDurumu, kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            } else if (tur === 'Gelir') {
                                // Gelir → Borç
                                expandedRows.push({
                                    key: `gelir-${o.odemeID}`, type: 'fatura',
                                    date: o.islemTarihi, label: o.aciklama || o.odemeTuru,
                                    rowBorc: o.miktar, rowAlacak: null,
                                    durum: o.odemeDurumu, kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            } else if (tur === 'Tahsilat' && o.odemeDurumu === 'Odendi') {
                                // Tahsilat + Odendi → Alacak
                                expandedRows.push({
                                    key: `tahsilat-${o.odemeID}`, type: 'makbuz',
                                    date: o.tahsilatTarihi || o.islemTarihi, label: 'Tahsilat',
                                    subLabel: o.aciklama || '',
                                    rowBorc: null, rowAlacak: o.miktar,
                                    durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            } else if (tur === 'Odeme' && o.odemeDurumu === 'Odendi') {
                                // Ödeme + Odendi → Borç
                                expandedRows.push({
                                    key: `odeme-${o.odemeID}`, type: 'fatura',
                                    date: o.tahsilatTarihi || o.islemTarihi, label: 'Ödeme',
                                    subLabel: o.aciklama || '',
                                    rowBorc: o.miktar, rowAlacak: null,
                                    durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            }
                        });

                        expandedRows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                        // Bakiye hesapla
                        let runningBakiye = 0;
                        const rowsWithBakiye = expandedRows.map(r => {
                            runningBakiye += (r.rowBorc || 0) - (r.rowAlacak || 0);
                            return { ...r, bakiye: runningBakiye };
                        });

                        // Filtreli ekstre
                        const filteredRows = rowsWithBakiye.filter(r => {
                            if (hareketFilters.vadeTarihiBaslangic && r.date < hareketFilters.vadeTarihiBaslangic) return false;
                            if (hareketFilters.vadeTarihiBitis && r.date > hareketFilters.vadeTarihiBitis) return false;
                            if (hareketFilters.odemeDurumu && r.durum !== hareketFilters.odemeDurumu) return false;
                            return true;
                        });

                        // Plan tab - tüm Tahsilat/Ödeme kayıtları (Bekliyor dahil)
                        const taksitler = odemeler.filter(o => {
                            if (!isSozlesmeGeliri(o) && (o.islemTipi === 'Tahsilat' || o.islemTipi === 'Odeme')) {
                                if (odemePlaniSozlesmeFiltre) return o.islemTakipNo === odemePlaniSozlesmeFiltre || (o.sozlesme?.sozlesmeTakipNo === odemePlaniSozlesmeFiltre);
                                return true;
                            }
                            return false;
                        }).sort((a, b) => {
                            if (!a.vadeTarihi && !b.vadeTarihi) return 0;
                            if (!a.vadeTarihi) return 1;
                            if (!b.vadeTarihi) return -1;
                            return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
                        });
                        const bekleyenPlanTaksitleri = sozlesmePlanTaksitleri.filter((t: any) => t.odemeDurumu === 'Bekliyor' && t.aktifMi !== false);
                        const toplamPlanTaksitMiktar = bekleyenPlanTaksitleri.reduce((s: number, t: any) => s + t.miktar, 0);
                        const toplamPlanlanan = taksitler.reduce((s, o) => s + o.miktar, 0) + toplamPlanTaksitMiktar;
                        const toplamTahsil = taksitler.filter(o => o.odemeDurumu === 'Odendi').reduce((s, o) => s + o.miktar, 0);
                        const toplamKalan = toplamPlanlanan - toplamTahsil;

                        return (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                        {/* Header */}
                                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                                            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <CreditCard size={20} style={{ color: '#cd6906' }} />
                                                Cari Hesap
                                            </h3>
                                            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>TOPLAM BORCA</div>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#dc2626' }}>₺{formatCurrency(genelToplamBorc)}</div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>TOPLAM ALACAK</div>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#16a34a' }}>₺{formatCurrency(genelToplamAlacak)}</div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>NET BAKİYE</div>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: netBakiye > 0 ? '#dc2626' : '#16a34a' }}>₺{formatCurrency(Math.abs(netBakiye))}</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Tab bar */}
                                        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
                                            {(['ekstre', 'plan'] as const).map(tab => (
                                                <button key={tab} onClick={() => setActiveHareketTab(tab)}
                                                    style={{ padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeHareketTab === tab ? 700 : 400, color: activeHareketTab === tab ? '#cd6906' : '#64748b', borderBottom: activeHareketTab === tab ? '2px solid #cd6906' : '2px solid transparent', marginBottom: -1, fontSize: 14 }}>
                                                    {tab === 'ekstre' ? 'Cari Ekstre' : 'Ödeme Planı'}
                                                </button>
                                            ))}
                                        </div>

                                        {/* EKSTRE TAB */}
                                        {activeHareketTab === 'ekstre' && (
                                            <div style={{ padding: '1rem 1.5rem' }}>
                                                {/* Filtreler */}
                                                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                    <div>
                                                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Başlangıç</label>
                                                        <TrDateInput value={hareketFilters.vadeTarihiBaslangic}
                                                            onChange={e => setHareketFilters(f => ({ ...f, vadeTarihiBaslangic: e.target.value }))}
                                                            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Bitiş</label>
                                                        <TrDateInput value={hareketFilters.vadeTarihiBitis}
                                                            onChange={e => setHareketFilters(f => ({ ...f, vadeTarihiBitis: e.target.value }))}
                                                            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Durum</label>
                                                        <select value={hareketFilters.odemeDurumu}
                                                            onChange={e => setHareketFilters(f => ({ ...f, odemeDurumu: e.target.value }))}
                                                            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }}>
                                                            <option value="">Tümü</option>
                                                            <option value="Odendi">Ödendi</option>
                                                            <option value="Bekliyor">Bekliyor</option>
                                                        </select>
                                                    </div>
                                                    {(hareketFilters.vadeTarihiBaslangic || hareketFilters.vadeTarihiBitis || hareketFilters.odemeDurumu) && (
                                                        <button onClick={() => setHareketFilters({ vadeTarihiBaslangic: '', vadeTarihiBitis: '', odemeTuru: '', odemeDurumu: '' })}
                                                            style={{ padding: '4px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', fontSize: 12, color: '#64748b' }}>
                                                            Temizle
                                                        </button>
                                                    )}
                                                </div>
                                                {/* Tablo */}
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>Tarih</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Açıklama</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Tür</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Borç</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Alacak</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Bakiye</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>İşlemler</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredRows.length === 0 ? (
                                                                <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Kayıt bulunamadı</td></tr>
                                                            ) : filteredRows.map((r, idx) => (
                                                                <tr key={r.key} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#475569' }}>{r.date ? toTRDate(r.date.slice(0, 10)) : '—'}</td>
                                                                    <td style={{ padding: '8px 12px' }}>
                                                                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.label}</div>
                                                                        {r.subLabel && <div style={{ fontSize: 11, color: '#64748b' }}>{r.subLabel}</div>}
                                                                        {r.kasaAdi && <div style={{ fontSize: 11, color: '#64748b' }}>{r.kasaAdi}</div>}
                                                                    </td>
                                                                    <td style={{ padding: '8px 12px' }}>
                                                                        {r.type === 'sozlesme' ? (
                                                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed', fontWeight: 600, border: '1px solid #ddd6fe' }}>Sözleşme</span>
                                                                        ) : (() => {
                                                                            const tur = r.orijinalOdeme?.islemTipi || '';
                                                                            if (tur === 'Gelir') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', fontWeight: 600, border: '1px solid #bbf7d0' }}>Gelir</span>;
                                                                            if (tur === 'Gider') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', fontWeight: 600, border: '1px solid #fecaca' }}>Gider</span>;
                                                                            if (tur === 'Tahsilat') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}>Tahsilat</span>;
                                                                            if (tur === 'Odeme') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#b45309', fontWeight: 600, border: '1px solid #fde68a' }}>Ödeme</span>;
                                                                            return null;
                                                                        })()}
                                                                    </td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{r.rowBorc != null ? `₺${formatCurrency(r.rowBorc)}` : ''}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>{r.rowAlacak != null ? `₺${formatCurrency(r.rowAlacak)}` : ''}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: r.bakiye > 0 ? '#dc2626' : '#16a34a' }}>₺{formatCurrency(Math.abs(r.bakiye))}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                        {r.orijinalOdeme && (
                                                                            <button onClick={() => handleOdemeEditAc(r.orijinalOdeme!)}
                                                                                style={{ padding: '3px 10px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                Düzenle
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                                                                <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, color: '#0f172a' }}>TOPLAM</td>
                                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>₺{formatCurrency(genelToplamBorc)}</td>
                                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>₺{formatCurrency(genelToplamAlacak)}</td>
                                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: netBakiye > 0 ? '#dc2626' : '#16a34a' }}>₺{formatCurrency(Math.abs(netBakiye))}</td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* ÖDEME PLANI TAB */}
                                        {activeHareketTab === 'plan' && (
                                            <div style={{ padding: '1rem 1.5rem' }}>
                                                {/* Sözleşme Filtresi */}
                                                {sozlesmeler.filter(s => s.sozlesmeDurumu === 'Sözleşme').length > 1 && (
                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4, display: 'block' }}>Sözleşme</label>
                                                        <select
                                                            value={odemePlaniSozlesmeFiltre}
                                                            onChange={e => { setOdemePlaniSozlesmeFiltre(e.target.value); setSelectedTaksitler(new Set()); }}
                                                            style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0f172a', background: '#fff', minWidth: 280, cursor: 'pointer' }}
                                                        >
                                                            <option value="">— Tüm sözleşmeler —</option>
                                                            {sozlesmeler.filter(s => s.sozlesmeDurumu === 'Sözleşme').map(s => {
                                                                const proje = s.daire?.blok?.proje?.projeAdi || '';
                                                                const blok = s.daire?.blok?.blokAdi || '';
                                                                const daire = s.daire?.daireNo || '';
                                                                let label = '';
                                                                if (proje) label += proje;
                                                                if (blok) label += (label ? ' · ' : '') + blok;
                                                                if (daire) label += (label ? ' · ' : '') + 'Daire ' + daire;
                                                                if (!label) label = s.sozlesmeTakipNo;
                                                                if (s.sozlesmeFiyati) label += ` · ${s.sozlesmeFiyati.toLocaleString('tr-TR')} ₺`;
                                                                return <option key={s.sozlesmeID} value={s.sozlesmeTakipNo}>{label}</option>;
                                                            })}
                                                        </select>
                                                    </div>
                                                )}
                                                {/* KPI */}
                                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                                    {[
                                                        { label: 'Toplam Planlanan', value: toplamPlanlanan, color: '#3b82f6' },
                                                        { label: 'Tahsil Edilen', value: toplamTahsil, color: '#16a34a' },
                                                        { label: 'Kalan', value: toplamKalan, color: '#dc2626' },
                                                    ].map(kpi => (
                                                        <div key={kpi.label} style={{ flex: 1, minWidth: 160, background: 'white', border: '1px solid #e2e8f0', borderLeft: `4px solid ${kpi.color}`, borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
                                                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{kpi.label}</div>
                                                            <div style={{ fontSize: 18, fontWeight: 700, color: kpi.color }}>₺{formatCurrency(kpi.value)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {/* Toolbar */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                        {selectedTaksitler.size > 0 && (
                                                            <button onClick={handleTopluSilTaksit} disabled={taksitTopluSilLoading}
                                                                style={{ padding: '6px 14px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                                                {taksitTopluSilLoading ? 'Siliniyor...' : `${selectedTaksitler.size} Taksiti Sil`}
                                                            </button>
                                                        )}
                                                    </div>
                                                    <button onClick={handleShowTopluPlanlaModal}
                                                        style={{ padding: '8px 18px', background: '#cd6906', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                                                        + Toplu Ödeme Planla
                                                    </button>
                                                </div>
                                                {/* Taksit tablosu */}
                                                {taksitler.length === 0 ? (
                                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Henüz ödeme planı oluşturulmamış</div>
                                                ) : (
                                                    <div style={{ overflowX: 'auto' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                            <thead>
                                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                    <th style={{ padding: '8px 12px', width: 32 }}>
                                                                        <input type="checkbox"
                                                                            checked={selectedTaksitler.size === taksitler.filter(t => t.odemeDurumu !== 'Odendi').length && taksitler.some(t => t.odemeDurumu !== 'Odendi')}
                                                                            onChange={e => {
                                                                                if (e.target.checked) setSelectedTaksitler(new Set(taksitler.filter(t => t.odemeDurumu !== 'Odendi').map(t => t.odemeID)));
                                                                                else setSelectedTaksitler(new Set());
                                                                            }} />
                                                                    </th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Açıklama</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Vade Tarihi</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Tutar</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Tür</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Durum</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Kasa</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>İşlemler</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {taksitler.map((t, idx) => (
                                                                    <tr key={t.odemeID} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                        <td style={{ padding: '8px 12px' }}>
                                                                            {t.odemeDurumu !== 'Odendi' && (
                                                                                <input type="checkbox" checked={selectedTaksitler.has(t.odemeID)}
                                                                                    onChange={e => {
                                                                                        const s = new Set(selectedTaksitler);
                                                                                        e.target.checked ? s.add(t.odemeID) : s.delete(t.odemeID);
                                                                                        setSelectedTaksitler(s);
                                                                                    }} />
                                                                            )}
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px', color: '#0f172a', fontWeight: 500 }}>{t.aciklama}</td>
                                                                        <td style={{ padding: '8px 12px', color: '#475569' }}>{t.vadeTarihi ? toTRDate(t.vadeTarihi.slice(0, 10)) : '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#334155' }}>₺{formatCurrency(t.miktar)}</td>
                                                                        <td style={{ padding: '8px 12px' }}>
                                                                            {(() => {
                                                                                const tur = t.islemTipi || '';
                                                                                if (tur === 'Gelir') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', fontWeight: 600, border: '1px solid #bbf7d0' }}>Gelir</span>;
                                                                                if (tur === 'Gider') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', fontWeight: 600, border: '1px solid #fecaca' }}>Gider</span>;
                                                                                if (tur === 'Tahsilat') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}>Tahsilat</span>;
                                                                                if (tur === 'Odeme') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#b45309', fontWeight: 600, border: '1px solid #fde68a' }}>Ödeme</span>;
                                                                                return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
                                                                            })()}
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px' }}>
                                                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: t.odemeDurumu === 'Odendi' ? '#dcfce7' : '#fef3c7', color: t.odemeDurumu === 'Odendi' ? '#16a34a' : '#b45309', fontWeight: 600 }}>
                                                                                {t.odemeDurumu === 'Odendi' ? 'Ödendi' : 'Bekliyor'}
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 12 }}>{t.kasa?.kasaAdi || '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                                                <button onClick={() => handleOdemeEditAc(t)}
                                                                                    style={{ padding: '4px 10px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                    Düzenle
                                                                                </button>
                                                                                {t.odemeDurumu !== 'Odendi' && (
                                                                                    <button onClick={() => handleOdemeYap(t)}
                                                                                        style={{ padding: '4px 10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                        {t.islemTipi === 'Odeme' ? 'Ödeme Yap' : 'Tahsilat Yap'}
                                                                                    </button>
                                                                                )}
                                                                                {t.odemeDurumu === 'Odendi' && (
                                                                                    <button onClick={() => handleTahsilatGeriAl(t)}
                                                                                        style={{ padding: '4px 10px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                        {t.islemTipi === 'Odeme' ? 'Ödemeyi Geri Al' : 'Tahsilatı Geri Al'}
                                                                                    </button>
                                                                                )}
                                                                                <button onClick={() => handleDeleteTaksit(t)}
                                                                                    style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                                                                                    Sil
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                {bekleyenPlanTaksitleri.map((t: any, idx: number) => (
                                                                    <tr key={`pt-${t.taksitID}`} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                        <td style={{ padding: '8px 12px' }}></td>
                                                                        <td style={{ padding: '8px 12px', color: '#0f172a', fontWeight: 500 }}>
                                                                            {t.aciklama}
                                                                            <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 12, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>Sözleşme Planı</span>
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px', color: '#475569' }}>{t.vadeTarihi ? toTRDate(t.vadeTarihi.slice(0, 10)) : '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#334155' }}>₺{formatCurrency(t.miktar)}</td>
                                                                        <td style={{ padding: '8px 12px' }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}>Tahsilat</span></td>
                                                                        <td style={{ padding: '8px 12px' }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#b45309', fontWeight: 600 }}>Bekliyor</span></td>
                                                                        <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 12 }}>{t.kasaAdi || '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                                                <button onClick={() => handlePlanTaksitTahsilatYap(t)}
                                                                                    style={{ padding: '4px 10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                    Tahsilat Yap
                                                                                </button>
                                                                                <button onClick={() => handleDeletePlanTaksit(t)}
                                                                                    style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                                                                                    Sil
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        );
                    })()}

                    {/* Kiralık Sözleşmeler & Emlak Sunumları */}
                    {activeMainTab === 'sozlesmeler' && (relatedRecords.kiraSozlesmeleri.length > 0 || relatedRecords.emlakSunumlari.length > 0) && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <div style={{
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '1rem',
                                    overflow: 'hidden',
                                    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{
                                        padding: '1.25rem 1.5rem',
                                        borderBottom: '1px solid #f1f5f9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <i className="fas fa-link" style={{ color: '#cd6906', fontSize: '1rem' }}></i>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                                            İlişkili Kayıtlar
                                        </h3>
                                    </div>
                                    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>

                                        {/* Kira Sözleşmeleri */}
                                        {relatedRecords.kiraSozlesmeleri.length > 0 && (
                                            <div style={{ flex: '1 1 340px' }}>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                                                    <i className="fas fa-key" style={{ marginRight: 6 }}></i>Kira Sözleşmeleri ({relatedRecords.kiraSozlesmeleri.length})
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                    {relatedRecords.kiraSozlesmeleri.map(ks => (
                                                        <div
                                                            key={ks.kiraSozlesmeID}
                                                            onClick={() => navigate(`/kiralik/sozlesme-detay/${ks.takipNo}`)}
                                                            style={{
                                                                padding: '0.875rem 1rem',
                                                                borderRadius: '0.75rem',
                                                                border: '1.5px solid #e2e8f0',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.75rem'
                                                            }}
                                                            onMouseOver={e => { e.currentTarget.style.borderColor = '#1a2340'; e.currentTarget.style.background = '#f8fafc'; }}
                                                            onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                                                        >
                                                            <div style={{
                                                                width: 36, height: 36, borderRadius: '0.5rem',
                                                                background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                            }}>
                                                                <i className="fas fa-file-contract" style={{ color: '#1a2340', fontSize: 14 }}></i>
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{ks.takipNo}</div>
                                                                {ks.gayrimenkulBaslik && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ks.gayrimenkulBaslik}</div>}
                                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                                                    {new Date(ks.baslangicTarihi).toLocaleDateString('tr-TR')} — {new Date(ks.bitisTarihi).toLocaleDateString('tr-TR')}
                                                                </div>
                                                            </div>
                                                            <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2340' }}>₺{ks.kiraBedeli.toLocaleString('tr-TR')}</div>
                                                                <span style={{
                                                                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                                                    background: ks.aktifMi ? '#dcfce7' : '#f1f5f9',
                                                                    color: ks.aktifMi ? '#15803d' : '#94a3b8'
                                                                }}>{ks.aktifMi ? 'Aktif' : 'Pasif'}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Emlak Sunumları */}
                                        {relatedRecords.emlakSunumlari.length > 0 && (
                                            <div style={{ flex: '1 1 340px' }}>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                                                    <i className="fas fa-building" style={{ marginRight: 6 }}></i>Emlak Sunumları ({relatedRecords.emlakSunumlari.length})
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                    {relatedRecords.emlakSunumlari.map(es => (
                                                        <div
                                                            key={es.sunumID}
                                                            onClick={() => navigate(`/emlak-sunumlari/${es.sunumID}`)}
                                                            style={{
                                                                padding: '0.875rem 1rem',
                                                                borderRadius: '0.75rem',
                                                                border: '1.5px solid #e2e8f0',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.75rem'
                                                            }}
                                                            onMouseOver={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.background = '#fffbf5'; }}
                                                            onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                                                        >
                                                            <div style={{
                                                                width: 36, height: 36, borderRadius: '0.5rem',
                                                                background: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                            }}>
                                                                <i className="fas fa-presentation" style={{ color: '#cd6906', fontSize: 14 }}></i>
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{es.takipNo}</div>
                                                                {es.baslik && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{es.baslik}</div>}
                                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{new Date(es.olusturmaTarihi).toLocaleDateString('tr-TR')}</div>
                                                            </div>
                                                            <div style={{ flexShrink: 0 }}>
                                                                <span style={{
                                                                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                                                    background: es.durum === 'Aktif' ? '#dcfce7' : es.durum === 'Tamamlandı' ? '#dbeafe' : es.durum === 'İptal' ? '#fee2e2' : '#f1f5f9',
                                                                    color: es.durum === 'Aktif' ? '#15803d' : es.durum === 'Tamamlandı' ? '#1d4ed8' : es.durum === 'İptal' ? '#dc2626' : '#64748b'
                                                                }}>{es.durum}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* AKTİVİTELER TAB */}
                    {activeMainTab === 'aktiviteler' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* Activity Feed - Notlar */}
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <h3 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <StickyNote size={20} style={{ color: '#64748b' }} />
                                    Aktivite Akışı
                                </h3>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto', 
                                    position: 'relative',
                                    paddingLeft: '2rem',
                                    maxHeight: '500px'
                                }}>
                                    {notlar.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#94a3b8', 
                                            padding: '2rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            Not bulunmamaktadır
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative' }}>
                                            {/* Timeline Line */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '0.25rem',
                                                top: '0.5rem',
                                                bottom: '0.5rem',
                                                width: '1px',
                                                background: '#e2e8f0'
                                            }} />
                                            
                                            {notlar.map((not, index) => (
                                                <div key={not.notID} style={{
                                                    position: 'relative',
                                                    paddingBottom: index === notlar.length - 1 ? 0 : '1rem'
                                                }}>
                                                    {/* Timeline Dot - Hollow Ring */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '-1.5rem',
                                                        top: '0.375rem',
                                                        width: '0.5rem',
                                                        height: '0.5rem',
                                                        borderRadius: '50%',
                                                        background: 'white',
                                                        border: '2px solid #4f46e5',
                                                        boxShadow: '0 0 0 2px white'
                                                    }} />
                                                    
                                                    {/* Note Content */}
                                                    <div style={{
                                                        marginLeft: '0.5rem'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '0.625rem',
                                                            letterSpacing: '0.02em',
                                                            color: '#94a3b8',
                                                            fontWeight: '500',
                                                            marginBottom: '0.25rem'
                                                        }}>
                                                            {not.ekleyen?.adSoyad} • {new Date(not.eklenmeTarihi).toLocaleString('tr-TR')}
                                                        </div>
                                                        <div style={{
                                                            background: '#f8fafc',
                                                            padding: '0.5rem 0.75rem',
                                                            borderRadius: '0.375rem',
                                                            borderLeft: '3px solid #e2e8f0',
                                                            fontSize: '0.875rem',
                                                            color: '#475569',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            {not.aciklama}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    )}

                    {/* DOSYALAR TAB */}
                    {activeMainTab === 'smsler' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <SmsGecmisi cariID={musteri?.cariID} refreshKey={smsRefreshKey} />
                        </Col>
                    </Row>
                    )}

                    {activeMainTab === 'dosyalar' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* File Cards - Dosyalar */}
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <h3 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <FileText size={20} style={{ color: '#64748b' }} />
                                    Ekli Dosyalar
                                </h3>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto',
                                    maxHeight: '500px'
                                }}>
                                    {dosyalar.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#94a3b8', 
                                            padding: '2rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            Dosya bulunmamaktadır
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}>
                                            {dosyalar.map((dosya) => {
                                                const ext = dosya.dosyaAdi.toLowerCase().split('.').pop() || '';
                                                const isPdf = ext === 'pdf';
                                                const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
                                                
                                                return (
                                                    <div 
                                                        key={dosya.dosyaID} 
                                                        className="file-list-item"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '0.75rem',
                                                            borderRadius: '0.5rem',
                                                            transition: 'background 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = '#f8fafc';
                                                            const actions = e.currentTarget.querySelector('.file-actions') as HTMLElement;
                                                            if (actions) actions.style.opacity = '1';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = 'transparent';
                                                            const actions = e.currentTarget.querySelector('.file-actions') as HTMLElement;
                                                            if (actions) actions.style.opacity = '0';
                                                        }}
                                                    >
                                                        {/* Sol taraf: İkon + Dosya Bilgisi */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.75rem',
                                                            flex: 1,
                                                            minWidth: 0
                                                        }}>
                                                            {/* Küçük İkon */}
                                                            <div style={{ flexShrink: 0 }}>
                                                                {isPdf ? (
                                                                    <FileText size={20} style={{ color: '#dc2626' }} />
                                                                ) : isImage ? (
                                                                    <FileImage size={20} style={{ color: '#10b981' }} />
                                                                ) : (
                                                                    <File size={20} style={{ color: '#64748b' }} />
                                                                )}
                                                            </div>
                                                            
                                                            {/* Dosya Adı ve Tarih */}
                                                            <div style={{ 
                                                                flex: 1,
                                                                minWidth: 0,
                                                                overflow: 'hidden'
                                                            }}>
                                                                <div style={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#0f172a',
                                                                    fontWeight: '500',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {dosya.aciklama}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '0.625rem',
                                                                    color: '#94a3b8',
                                                                    marginTop: '0.125rem'
                                                                }}>
                                                                    {new Date(dosya.eklenmeTarihi).toLocaleDateString('tr-TR')} · {ext.toUpperCase()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Sağ taraf: Hover'da görünen butonlar */}
                                                        <div 
                                                            className="file-actions"
                                                            style={{
                                                                display: 'flex',
                                                                gap: '0.5rem',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {(isPdf || isImage) && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreview(dosya.dosyaID, dosya.dosyaAdi);
                                                                    }}
                                                                    style={{
                                                                        padding: '0.375rem',
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        borderRadius: '0.375rem',
                                                                        color: '#10b981',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        transition: 'background 0.2s'
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f0fdf4'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                    title="Önizle"
                                                                >
                                                                    <Eye size={16} />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDosyaDownload(dosya.dosyaID);
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderRadius: '0.375rem',
                                                                    color: '#4f46e5',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseOver={(e) => e.currentTarget.style.background = '#eef2ff'}
                                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                title="İndir"
                                                            >
                                                                <Download size={16} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    // Sil fonksiyonu buraya
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderRadius: '0.375rem',
                                                                    color: '#ef4444',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseOver={(e) => e.currentTarget.style.background = '#fef2f2'}
                                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                title="Sil"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    )}
                </div>
            </section>

            {/* Müşteri Düzenleme Drawer */}
            {showModal && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); if (id === 'yeni') navigate('/musteriler'); } }}
                >
                    {/* Backdrop */}
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
                    {/* Drawer */}
                    <div style={{ position: 'relative', width: '100%', maxWidth: 1020, height: '92vh', maxHeight: '92vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflowY: 'hidden' }}>
                        {/* Sticky Header */}
                        <div style={{ flexShrink: 0, padding: '20px 24px 0', borderBottom: '1px solid #f3f4f6', background: '#fff', zIndex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div>
                                    <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{id === 'yeni' ? 'Yeni Müşteri' : 'Müşteri Düzenle'}</h2>
                                </div>
                                <button
                                    onClick={() => { setShowModal(false); if (id === 'yeni') navigate('/musteriler'); }}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center' }}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: 24 }}>
                                {(['temel', 'firma'] as const).map((tab) => {
                                    const labels = { temel: 'Temel Bilgiler', firma: 'Firma Bilgileri' };
                                    const active = activeFormTab === tab;
                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setActiveFormTab(tab)}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 12px',
                                                fontSize: 13, fontWeight: 500,
                                                color: active ? '#111827' : '#6b7280',
                                                borderBottom: active ? '2px solid #111827' : '2px solid transparent',
                                                transition: 'color 0.15s, border-color 0.15s',
                                            }}
                                        >{labels[tab]}</button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Scrollable Body */}
                        <Form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 24, flex: 1 }}>

                                {/* Tab: Temel Bilgiler */}
                                {activeFormTab === 'temel' && (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20 }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    Adı Soyadı <span style={{ color: '#f43f5e' }}>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.adiSoyadi}
                                                    onChange={(e) => setFormData({ ...formData, adiSoyadi: e.target.value })}
                                                    required maxLength={100}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                    onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>TC Kimlik No</label>
                                                <input
                                                    type="text"
                                                    value={formData.tc || ''}
                                                    onChange={(e) => setFormData({ ...formData, tc: e.target.value || null })}
                                                    maxLength={11}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                    onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                />
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Tür</label>
                                                    <select
                                                        value={formData.turu}
                                                        onChange={(e) => setFormData({ ...formData, turu: e.target.value })}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    >
                                                        <option value="Müşteri">Müşteri</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Alt Kategori</label>
                                                    <select
                                                        value={formData.turu2 || ''}
                                                        onChange={(e) => setFormData({ ...formData, turu2: e.target.value || null })}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    >
                                                        <option value="">Seçiniz...</option>
                                                        {cariAltKategoriler.map(k => (
                                                            <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    Telefon 1 <span style={{ color: '#f43f5e' }}>*</span>
                                                    {iysAktarimAktif && (iysIzinlerMd.some(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                                        ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                                        : formData.telefon1 && <button type="button" onClick={() => setIysModalMd({ iletisimTipi: 'TELEFON1', iletisimVerisi: formData.telefon1 })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                                </label>
                                                <IysKilitliAlan
                                                    aktifIzin={iysIzinlerMd.find(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                                    cariID={musteri?.cariID ?? 0}
                                                    takipNo={musteri?.takipNo ?? ''}
                                                    ekleyenID={user?.kullaniciID ?? 0}
                                                    iletisimTipiLabel="Telefon 1"
                                                    onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri?.cariID}`); setIysIzinlerMd(r.data); } catch {} }}
                                                    disabled={!iysAktarimAktif}
                                                >{(readOnly) => (
                                                    <input
                                                        type="text"
                                                        value={formatPhoneNumber(formData.telefon1)}
                                                        onChange={(e) => setFormData({ ...formData, telefon1: unformatPhoneNumber(e.target.value) })}
                                                        required maxLength={14} placeholder="(5XX)XXX-XX-XX"
                                                        readOnly={readOnly}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: readOnly ? '#f9fafb' : '#fff', color: '#111827', boxSizing: 'border-box', cursor: readOnly ? 'not-allowed' : undefined }}
                                                        onFocus={e => { if (!readOnly) { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; } }}
                                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                    />
                                                )}</IysKilitliAlan>
                                            </div>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    Telefon 2
                                                    {iysAktarimAktif && (iysIzinlerMd.some(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                                        ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                                        : formData.telefon2 && <button type="button" onClick={() => setIysModalMd({ iletisimTipi: 'TELEFON2', iletisimVerisi: formData.telefon2! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                                </label>
                                                <IysKilitliAlan
                                                    aktifIzin={iysIzinlerMd.find(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                                    cariID={musteri?.cariID ?? 0}
                                                    takipNo={musteri?.takipNo ?? ''}
                                                    ekleyenID={user?.kullaniciID ?? 0}
                                                    iletisimTipiLabel="Telefon 2"
                                                    onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri?.cariID}`); setIysIzinlerMd(r.data); } catch {} }}
                                                    disabled={!iysAktarimAktif}
                                                >{(readOnly) => (
                                                    <input
                                                        type="text"
                                                        value={formData.telefon2 ? formatPhoneNumber(formData.telefon2) : ''}
                                                        onChange={(e) => setFormData({ ...formData, telefon2: e.target.value ? unformatPhoneNumber(e.target.value) : null })}
                                                        maxLength={14} placeholder="(5XX)XXX-XX-XX"
                                                        readOnly={readOnly}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: readOnly ? '#f9fafb' : '#fff', color: '#111827', boxSizing: 'border-box', cursor: readOnly ? 'not-allowed' : undefined }}
                                                        onFocus={e => { if (!readOnly) { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; } }}
                                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                    />
                                                )}</IysKilitliAlan>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    E-Posta
                                                    {iysAktarimAktif && (iysIzinlerMd.some(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                                        ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                                        : formData.ePosta && <button type="button" onClick={() => setIysModalMd({ iletisimTipi: 'EPOSTA', iletisimVerisi: formData.ePosta! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>IYS İzni Al</button>)}
                                                </label>
                                                <IysKilitliAlan
                                                    aktifIzin={iysIzinlerMd.find(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                                    cariID={musteri?.cariID ?? 0}
                                                    takipNo={musteri?.takipNo ?? ''}
                                                    ekleyenID={user?.kullaniciID ?? 0}
                                                    iletisimTipiLabel="E-Posta"
                                                    onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri?.cariID}`); setIysIzinlerMd(r.data); } catch {} }}
                                                    disabled={!iysAktarimAktif}
                                                >{(readOnly) => (
                                                    <input
                                                        type="email"
                                                        value={formData.ePosta || ''}
                                                        onChange={(e) => setFormData({ ...formData, ePosta: e.target.value || null })}
                                                        maxLength={100}
                                                        readOnly={readOnly}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: readOnly ? '#f9fafb' : '#fff', color: '#111827', boxSizing: 'border-box', cursor: readOnly ? 'not-allowed' : undefined }}
                                                        onFocus={e => { if (!readOnly) { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; } }}
                                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                    />
                                                )}</IysKilitliAlan>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Adres</label>
                                                <input
                                                    type="text"
                                                    value={formData.adres || ''}
                                                    onChange={(e) => setFormData({ ...formData, adres: e.target.value || null })}
                                                    maxLength={500}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                    onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Tab: Firma Bilgileri */}
                                {activeFormTab === 'firma' && (
                                    <>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Firma Adı</label>
                                            <input
                                                type="text"
                                                value={formData.firmaAdi || ''}
                                                onChange={(e) => setFormData({ ...formData, firmaAdi: e.target.value || null })}
                                                maxLength={100}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Vergi Dairesi</label>
                                            <input
                                                type="text"
                                                value={formData.cariVD || ''}
                                                onChange={(e) => setFormData({ ...formData, cariVD: e.target.value || null })}
                                                maxLength={100}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Vergi No</label>
                                            <input
                                                type="text"
                                                value={formData.cariVN || ''}
                                                onChange={(e) => setFormData({ ...formData, cariVN: e.target.value || null })}
                                                maxLength={50}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* IYS Kayıtları */}
                                <div style={{ padding: '0 28px 4px' }}>
                                    <IysKayitListesi izinler={iysIzinlerMd} />
                                </div>

                            </div>

                            {/* Sticky Footer */}
                            <div style={{ flexShrink: 0, padding: '20px 28px', borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div
                                        onClick={() => setFormData({ ...formData, aktifMi: !formData.aktifMi })}
                                        style={{ width: 44, height: 24, borderRadius: 12, background: formData.aktifMi ? '#cd6906' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                                    >
                                        <div style={{ position: 'absolute', top: 3, left: formData.aktifMi ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                    </div>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Aktif</span>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); if (id === 'yeni') navigate('/musteriler'); }}
                                    style={{ padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                >İptal</button>
                                <button
                                    type="submit"
                                    style={{ padding: '10px 24px', background: '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.15s, box-shadow 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                ><Check size={16} />Kaydet</button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            )}

            {/* Not Ekleme Modal */}
            <Modal show={showNotModal} onHide={() => setShowNotModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Not Ekle</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleNotSubmit}>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>Açıklama</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={notFormData.aciklama}
                                onChange={(e) => setNotFormData({ aciklama: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowNotModal(false)}>
                            İptal
                        </Button>
                        <Button variant="primary" type="submit">
                            Kaydet
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Dosya Ekleme Modal */}
            <Modal show={showDosyaModal} onHide={() => setShowDosyaModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Dosya Ekle</Modal.Title>
                </Modal.Header>
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!dosyaFormData.dosya || !musteri?.takipNo) {
                        toast.error('Lütfen bir dosya seçin');
                        return;
                    }

                    const formData = new FormData();
                    formData.append('file', dosyaFormData.dosya);
                    formData.append('takipNo', musteri.takipNo);
                    formData.append('aciklama', dosyaFormData.aciklama);

                    try {
                        await axiosInstance.post('/api/Dosyalar/upload', formData);
                        toast.success('Dosya başarıyla yüklendi');
                        setShowDosyaModal(false);
                        setDosyaFormData({ dosya: null, aciklama: '' });
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                        fetchDosyalar(); // Dosya listesini güncelle
                    } catch (error) {
                        console.error('Dosya yükleme hatası:', error);
                        toast.error('Dosya yüklenirken bir hata oluştu');
                    }
                }}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Dosya Seç</Form.Label>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const file = e.target.files?.[0] || null;
                                    setDosyaFormData(prev => ({ ...prev, dosya: file }));
                                }}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Açıklama</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={dosyaFormData.aciklama}
                                onChange={(e) => setDosyaFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDosyaModal(false)}>
                            İptal
                        </Button>
                        <Button variant="primary" type="submit">
                            Yükle
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Dosya Önizleme Modal */}
            <Modal 
                show={showPreviewModal} 
                onHide={() => {
                    setShowPreviewModal(false);
                    if (previewUrl) {
                        window.URL.revokeObjectURL(previewUrl);
                    }
                    setPreviewUrl('');
                }}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Dosya Önizleme</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ 
                    padding: 0, 
                    minHeight: '70vh', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: '#f8fafc'
                }}>
                    {previewType.startsWith('image/') ? (
                        <div style={{
                            width: '100%',
                            height: '70vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '1rem'
                        }}>
                            <img 
                                src={previewUrl} 
                                alt="Önizleme" 
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    ) : previewType === 'application/pdf' ? (
                        <iframe
                            src={previewUrl}
                            style={{
                                width: '100%',
                                height: '70vh',
                                border: 'none'
                            }}
                            title="PDF Önizleme"
                        />
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            color: '#94a3b8',
                            padding: '2rem'
                        }}>
                            Bu dosya türü önizlenemez
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowPreviewModal(false);
                        if (previewUrl) {
                            window.URL.revokeObjectURL(previewUrl);
                        }
                        setPreviewUrl('');
                    }}>
                        Kapat
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ödeme Düzenle Modal */}
            {showOdemeEditModal && selectedOdeme && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowOdemeEditModal(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Edit size={17} style={{ color: '#cd6906' }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Ödeme Düzenle</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>#{selectedOdeme.islemTakipNo}</div>
                                </div>
                            </div>
                            <button onClick={() => setShowOdemeEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Body */}
                        <form onSubmit={handleOdemeGuncelle}>
                            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Vade Tarihi</label>
                                        <TrDateInput value={odemeEditForm.vadeTarihi} onChange={e => setOdemeEditForm(p => ({ ...p, vadeTarihi: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Tahsilat Tarihi</label>
                                        <TrDateInput value={odemeEditForm.tahsilatTarihi} onChange={e => setOdemeEditForm(p => ({ ...p, tahsilatTarihi: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Tutar <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <input type="number" step="0.01" min="0" required value={odemeEditForm.miktar} onChange={e => setOdemeEditForm(p => ({ ...p, miktar: parseFloat(e.target.value) || 0 }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Ödeme Türü</label>
                                        <select value={odemeEditForm.odemeTuru} onChange={e => setOdemeEditForm(p => ({ ...p, odemeTuru: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Taksit">Taksit</option>
                                            <option value="Peşinat">Peşinat</option>
                                            <option value="Senet">Senet</option>
                                            <option value="Nakit">Nakit</option>
                                            <option value="KrediKarti">Kredi Kartı</option>
                                            <option value="Havale">Havale/EFT</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Durum</label>
                                        <select value={odemeEditForm.odemeDurumu} onChange={e => setOdemeEditForm(p => ({ ...p, odemeDurumu: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Bekliyor">Bekliyor</option>
                                            <option value="Odendi">Ödendi</option>
                                        </select>
                                    </div>
                                    {odemeEditForm.odemeDurumu === 'Odendi' && (
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Kasa <span style={{ color: '#f43f5e' }}>*</span></label>
                                            <select required value={odemeEditForm.kasaID ?? ''} onChange={e => setOdemeEditForm(p => ({ ...p, kasaID: e.target.value ? Number(e.target.value) : null }))}
                                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                                <option value="">— Seçiniz —</option>
                                                {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Açıklama</label>
                                    <textarea rows={3} value={odemeEditForm.aciklama} onChange={e => setOdemeEditForm(p => ({ ...p, aciklama: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                </div>
                            </div>
                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" onClick={() => setShowOdemeEditModal(false)}
                                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                                    İptal
                                </button>
                                <button type="submit"
                                    style={{ padding: '9px 22px', background: '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; }}>
                                    <Check size={15} /> Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toplu Ödeme Planla Modal */}
            <Modal show={showTopluPlanlaModal} onHide={() => setShowTopluPlanlaModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Toplu Ödeme Planla</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Toplam Tutar (₺) *</Form.Label>
                            <Form.Control type="text" placeholder="0" value={topluPlanlaFormData.toplamTutar}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, toplamTutar: formatMoneyInput(e.target.value) }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Taksit Sayısı *</Form.Label>
                            <Form.Control type="number" min={1} value={topluPlanlaFormData.taksitSayisi}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, taksitSayisi: e.target.value }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Başlama Tarihi *</Form.Label>
                            <Form.Control type="text" placeholder="GG.AA.YYYY" maxLength={10} value={topluPlanlaFormData.baslamaTarihi}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, baslamaTarihi: maskTRDate(e.target.value) }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Aralık (Ay)</Form.Label>
                            <Form.Control type="number" min={1} value={topluPlanlaFormData.aralikAy}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, aralikAy: e.target.value }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Ödeme Türü</Form.Label>
                            <Form.Select value={topluPlanlaFormData.odemeTuru} onChange={e => setTopluPlanlaFormData(f => ({ ...f, odemeTuru: e.target.value }))}>
                                <option value="Nakit">Nakit</option>
                                <option value="Senet">Senet</option>
                                <option value="Cek">Çek</option>
                                <option value="Takas">Takas</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Açıklama</Form.Label>
                            <Form.Control type="text" value={topluPlanlaFormData.aciklama}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, aciklama: e.target.value }))} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTopluPlanlaModal(false)}>İptal</Button>
                    <Button variant="primary" style={{ background: '#cd6906', borderColor: '#cd6906' }} onClick={handleSaveTopluPlanla} disabled={saving}>
                        {saving ? 'Kaydediliyor...' : 'Planla'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Tahsilat Yap Modal */}
            {showOdemeYapModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowOdemeYapModal(false); setIsPlanTaksitOdeme(false); setPlanTaksitForOdeme(null); }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fas fa-plus" style={{ color: '#cd6906', fontSize: 14 }}></i>
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Tahsilat Ekle</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                        {odemeYapTaksit && <><strong>{odemeYapTaksit.aciklama}</strong> — ₺{formatCurrency(odemeYapTaksit.miktar)}</>}
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => { setShowOdemeYapModal(false); setIsPlanTaksitOdeme(false); setPlanTaksitForOdeme(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Body */}
                        <form onSubmit={e => { e.preventDefault(); handleSaveOdemeYap(); }}>
                            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflowY: 'auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Tahsilat Tarihi <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <TrDateInput required value={odemeYapFormData.tarih}
                                            onChange={e => setOdemeYapFormData(f => ({ ...f, tarih: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Ödeme Türü</label>
                                        <select value={odemeYapFormData.odemeTuru}
                                            onChange={e => setOdemeYapFormData(f => ({ ...f, odemeTuru: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Nakit">Nakit</option>
                                            <option value="Senet">Senet</option>
                                            <option value="Cek">Çek</option>
                                            <option value="Takas">Takas</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Kasa <span style={{ color: '#f43f5e' }}>*</span></label>
                                    <select required value={odemeYapFormData.kasaID}
                                        onChange={e => setOdemeYapFormData(f => ({ ...f, kasaID: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                        <option value="">— Kasa seçin —</option>
                                        {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>)}
                                    </select>
                                </div>
                                {(sozlesmeler.length > 0 || relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0) && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                            İlişkili Sözleşme <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(opsiyonel)</span>
                                        </label>
                                        <select
                                            value={odemeYapFormData.sozlesmeSecimi ? `s:${odemeYapFormData.sozlesmeSecimi}` : odemeYapFormData.kiraSozlesmeSecimi ? `k:${odemeYapFormData.kiraSozlesmeSecimi}` : ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (!val) setOdemeYapFormData(f => ({ ...f, sozlesmeSecimi: '', kiraSozlesmeSecimi: '' }));
                                                else if (val.startsWith('s:')) setOdemeYapFormData(f => ({ ...f, sozlesmeSecimi: val.slice(2), kiraSozlesmeSecimi: '' }));
                                                else if (val.startsWith('k:')) setOdemeYapFormData(f => ({ ...f, kiraSozlesmeSecimi: val.slice(2), sozlesmeSecimi: '' }));
                                            }}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="">— Sözleşme seçilmedi —</option>
                                            {sozlesmeler.length > 0 && (
                                                <optgroup label="Satış Sözleşmeleri">
                                                    {sozlesmeler.map(s => (
                                                        <option key={s.sozlesmeID} value={`s:${s.sozlesmeTakipNo}`}>
                                                            {s.sozlesmeTakipNo} — {s.daire?.blok?.proje?.projeAdi || ''} {s.daire?.daireNo ? `/ Daire ${s.daire.daireNo}` : ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                            {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0 && (
                                                <optgroup label="Kira Sözleşmeleri">
                                                    {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).map(ks => (
                                                        <option key={ks.kiraSozlesmeID} value={`k:${ks.takipNo}`}>
                                                            {ks.takipNo} — {ks.gayrimenkulBaslik || ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Açıklama</label>
                                    <textarea rows={3} value={odemeYapFormData.aciklama}
                                        onChange={e => setOdemeYapFormData(f => ({ ...f, aciklama: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                </div>
                            </div>
                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" onClick={() => { setShowOdemeYapModal(false); setIsPlanTaksitOdeme(false); setPlanTaksitForOdeme(null); }}
                                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                                    İptal
                                </button>
                                <button type="submit" disabled={saving}
                                    style={{ padding: '9px 22px', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#cd6906', display: 'flex', alignItems: 'center', gap: 7 }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                                    <Check size={15} /> {saving ? 'Kaydediliyor...' : 'Tahsilatı Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Tahsilat / Ödeme Ekle Modal */}
            {showHareketModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowHareketModal(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 10,
                                    background: hareketFormData.odemeYonu === 'Tahsilat' ? '#fff7ed' : '#fef2f2',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <i className={`fas ${hareketFormData.odemeYonu === 'Tahsilat' ? 'fa-plus' : 'fa-minus'}`}
                                        style={{ color: hareketFormData.odemeYonu === 'Tahsilat' ? '#cd6906' : '#dc2626', fontSize: 14 }}></i>
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                                        {hareketFormData.odemeYonu === 'Tahsilat' ? 'Tahsilat Ekle' : 'Ödeme Ekle'}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>{musteri?.adiSoyadi}</div>
                                </div>
                            </div>
                            <button onClick={() => setShowHareketModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Body */}
                        <form onSubmit={e => { e.preventDefault(); handleSaveHareket(); }}>
                            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflowY: 'auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Tutar <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <input type="number" step="0.01" min="0" required
                                            value={hareketFormData.miktar}
                                            onChange={e => setHareketFormData(p => ({ ...p, miktar: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Vade Tarihi <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <TrDateInput required
                                            value={hareketFormData.vadeTarihi}
                                            onChange={e => setHareketFormData(p => ({ ...p, vadeTarihi: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Ödeme Türü</label>
                                        <select value={hareketFormData.odemeTuru}
                                            onChange={e => setHareketFormData(p => ({ ...p, odemeTuru: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Nakit">Nakit</option>
                                            <option value="Senet">Senet</option>
                                            <option value="Cek">Çek</option>
                                            <option value="Takas">Takas</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Durum</label>
                                        <select value={hareketFormData.odemeDurumu}
                                            onChange={e => setHareketFormData(p => ({ ...p, odemeDurumu: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Bekliyor">Bekliyor</option>
                                            <option value="Odendi">Ödendi</option>
                                        </select>
                                    </div>
                                </div>
                                {hareketFormData.odemeDurumu === 'Odendi' && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Kasa <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <select value={hareketFormData.kasaID}
                                            onChange={e => setHareketFormData(p => ({ ...p, kasaID: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="">— Kasa seçin —</option>
                                            {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>)}
                                        </select>
                                    </div>
                                )}
                                {/* Satış Sözleşmesi Seçimi */}
                                {(sozlesmeler.length > 0 || relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0) && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                            İlişkili Sözleşme <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(opsiyonel)</span>
                                        </label>
                                        <select
                                            value={hareketFormData.sozlesmeSecimi || hareketFormData.kiraSozlesmeSecimi
                                                ? (hareketFormData.sozlesmeSecimi ? `s:${hareketFormData.sozlesmeSecimi}` : `k:${hareketFormData.kiraSozlesmeSecimi}`)
                                                : ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (!val) {
                                                    setHareketFormData(p => ({ ...p, sozlesmeSecimi: '', kiraSozlesmeSecimi: '' }));
                                                } else if (val.startsWith('s:')) {
                                                    setHareketFormData(p => ({ ...p, sozlesmeSecimi: val.slice(2), kiraSozlesmeSecimi: '' }));
                                                } else if (val.startsWith('k:')) {
                                                    setHareketFormData(p => ({ ...p, kiraSozlesmeSecimi: val.slice(2), sozlesmeSecimi: '' }));
                                                }
                                            }}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="">— Sözleşme seçilmedi —</option>
                                            {sozlesmeler.length > 0 && (
                                                <optgroup label="Satış Sözleşmeleri">
                                                    {sozlesmeler.map(s => (
                                                        <option key={s.sozlesmeID} value={`s:${s.sozlesmeTakipNo}`}>
                                                            {s.sozlesmeTakipNo} — {s.daire?.blok?.proje?.projeAdi || ''} {s.daire?.daireNo ? `/ Daire ${s.daire.daireNo}` : ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                            {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0 && (
                                                <optgroup label="Kira Sözleşmeleri">
                                                    {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).map(ks => (
                                                        <option key={ks.kiraSozlesmeID} value={`k:${ks.takipNo}`}>
                                                            {ks.takipNo} — {ks.gayrimenkulBaslik || ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                        {(hareketFormData.sozlesmeSecimi || hareketFormData.kiraSozlesmeSecimi) && (
                                            <div style={{ marginTop: 6, fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <i className="fas fa-link" style={{ fontSize: 10 }}></i> Bu hareket seçili sözleşme ile ilişkilendirilecek
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Açıklama</label>
                                    <textarea rows={3} value={hareketFormData.aciklama}
                                        onChange={e => setHareketFormData(p => ({ ...p, aciklama: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                </div>
                            </div>
                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" onClick={() => setShowHareketModal(false)}
                                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                                    İptal
                                </button>
                                <button type="submit" disabled={hareketSaving}
                                    style={{
                                        padding: '9px 22px', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                        background: hareketFormData.odemeYonu === 'Tahsilat' ? '#cd6906' : '#dc2626',
                                        display: 'flex', alignItems: 'center', gap: 7,
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                                    <Check size={15} /> {hareketSaving ? 'Kaydediliyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showSmsModal && musteri && (
                <SmsGonderModal
                    onClose={() => setShowSmsModal(false)}
                    onSent={() => setSmsRefreshKey(k => k + 1)}
                    telefonlar={[
                        ...(musteri.telefon1 ? [{ numara: musteri.telefon1, etiket: 'Telefon 1' }] : []),
                        ...(musteri.telefon2 ? [{ numara: musteri.telefon2, etiket: 'Telefon 2' }] : []),
                    ]}
                    cariID={musteri.cariID}
                    musteriAdi={musteri.adiSoyadi}
                />
            )}

            {iysModalMd && musteri?.cariID && (
                <IysIzinModal
                    cariID={musteri.cariID}
                    iletisimTipi={iysModalMd.iletisimTipi}
                    iletisimVerisi={iysModalMd.iletisimVerisi}
                    onClose={() => setIysModalMd(null)}
                    onSuccess={async () => {
                        setIysModalMd(null);
                        try {
                            const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri.cariID}`);
                            setIysIzinlerMd(r.data);
                        } catch {}
                    }}
                />
            )}
        </div>
    );
};

export default MusteriDetay;
